import axios, { AxiosInstance, AxiosError } from 'axios';
import type { EngagementArchivePreview } from '../utils/engagementArchiveModel';
import { offerPausedResume } from '../store/pausedResumeStore';
import { offerCoverageRequest } from '../store/coverageStore';
import type { CoverageApprovalScope, CoverageRequestInfo, TicketCoverageInfo, UncoveredCustomer } from '../utils/coverageModel';
import { toast } from '../store/toastStore';
import i18n from '../i18n';
import { authSwitchedError, isAuthSwitchedElsewhere } from '../utils/authTabSync';
import { israelToday } from '../utils/israelTime';

/** BQ-001/BQ-002 — charges already approved are left alone; tell the manager (D-115). */
function announceBillingWarnings<T>(res: T, kind: 'price' | 'cancel'): T {
  const warnings = (res as { billing_warnings?: Array<{ source_key: string | null; title: string; reason?: string }> } | undefined)?.billing_warnings ?? [];
  for (const w of warnings) {
    if (w.source_key === 'purchase') { // hour-bank purchase charge: a dedicated text, never the raw key (BQ-044)
      toast('warning', i18n.t(kind === 'cancel' ? 'billing.bankPurchaseKeptCancelled' : 'billing.bankPurchaseKeptPrice'));
      continue;
    }
    toast('warning', i18n.t(w.reason === 'edited' ? 'billing.scheduleLockedEdited' : w.reason === 'work_done' ? 'billing.scheduleKeptWorkDone' : kind === 'price' ? 'billing.scheduleLockedPriceChanged' : 'billing.scheduleLockedCancelled', { key: w.source_key ?? w.title }));
  }
  return res;
}
import type { TopEmployeesData } from '../components/dashboard/topEmployeeModel';
import type { ReminderRow, reminderPayload } from '../utils/reminderModel';
import type { LeadJourneyFacts } from '../components/leads/leadJourneyModel';
import type { BusinessProfit, CustomerProfit, ProfitPeriodParams, ProjectProfit, ServersReport } from '../components/profitability/profitabilityModel';
import type { WorkspaceJournalEntryDto } from '../types/workspace';
import type { MonthApprovalStatus, MonthLogsResponse, MonthApprovalOverview } from '../components/monthApproval/monthApprovalModel';
import { uploadProgressBus } from '../utils/attachPasteDrop';
import type {
  HourBankAggregate,
  HourBankPurchase,
  ConsumptionPreview,
  Payment,
  BillingAdjustment,
  DebtAging,
  PaymentProviderInfo,
  ExtraHourRequest,
  EngagementRate,
} from '../types/billing';
import type {
  PendingActionsResponse,
  PortalRecentNotification,
  PortalRequest,
  PortalConversation,
  PortalTicketApproval,
  TicketApprovalStatus,
  PortalRequestMessage,
  CreatePortalRequestInput,
  PortalDashboardData,
  NotificationPreferencesResponse,
  NotificationPreferenceUpdate,
  ReportFilterValues,
  ReportRunResult,
  DashboardTemplate,
  DiagnosticsData,
} from '../types/clientWorkspace';
import type {
  Accountant,
  ApiResponse,
  AuthResponse,
  LoginCredentials,
  Ticket,
  TicketAgent,
  TicketAttachment,
  TicketTimeline,
  ConversationSummary,
  ConversationGroup,
  TicketMentionResult,
  CreateCommentDto,
  WorkLog,
  CreateWorkLogDto,
  CreateTicketDto,
  User,
  Organization,
  DashboardSummary,
  BillingSummary,
  VoiceEntry,
  Project,
  ProjectAssignments,
  ProjectManagerAssignment,
  ProjectTeamMember,
  Milestone,
  MilestoneMetrics,
  MilestonePermissions,
  MyMilestoneAssignee,
  SiblingMilestone,
  ProjectStats,
  ProjectFinancials,
  ProjectHours,
  ProjectTeamPerson,
  Notification,
  ApiKeyInfo,
  WebhookEndpoint,
  WebhookDelivery,
  SearchResponse,
  BurndownDataPoint,
  BudgetVsActualData,
  MonthlyRevenuePoint,
  ProfitabilityData,
  EmployeeCostVersion,
  HoursByAgentData,
  TicketsByPriorityData,
  HoursByTicketData,
  AgentSummaryData,
  IntegrationPreset,
  IntegrationInstance,
  IntegrationFieldMapping,
  IntegrationPipelineStep,
  IntegrationTarget,
  IntegrationSecretKey,
  AttendanceRecord,
  AttendanceDailySummary,
  MonthTravel,
  AttendancePolicy,
  AttendanceStatus,
  AttendanceVsTicketsRow,
  TeamDocPeriod,
  TeamDocumentation,
  WorkLogSuggestionsData,
  AcceptWorkLogSuggestionDto,
  PendingLllHour,
  IntegrationInboundLog,
  TestPipelineResponse,
  BlocklistRule,
  EmailTemplate,
  EmailTemplateMergeSchema,
  EmailTemplatePreview,
  EmailTemplateAsset,
  Lead,
  LeadProduct,
  LeadCommunication,
  Product,
  Quote,
  QuoteLineItem,
  Dashboard,
  DashboardWidget,
  WidgetCatalogItem,
  Contact,
  ContactAccessType,
  ContactInteraction,
  BugReportContext,
  AgentReadableContext,
  CreateBugReportPayload,
  UpdateBugReportPayload,
  BugReportListItem,
  MyBugReport,
  BugReportStatus,
  BugReportVerdict,
  BugReportTriage,
  PermissionRule,
  PermissionName,
  PermissionCatalogItem,
  PermissionGroup,
  PermissionGroupMember,
  PermissionGrant,
  PermissionGrantEffect,
  PermissionSubjectType,
  ScreenOverrideLevel,
  ScreenOverridesOfUser,
  ScreenDataScope,
  PortalAccess,
  PortalOrg,
  PortalEngagement,
  PortalCharacterization,
  PortalMilestone,
  PortalDocument,
  PortalHoursRow,
  PortalObligation,
  PortalSummaryData,
  PortalPermissionsResponse,
  AuditTrailEntry,
  EmployeeProfile,
  EmployeeSystemRoles,
  EmployeeRoleKind,
  EmployeeDeactivationPreview,
  EmployeeDeactivateBody,
  Engagement,
  EngagementType,
  EngagementStatus,
  EngagementChild,
  TypedEngagementTerms,
  ServiceContract,
  HourBank,
  HourBankSnapshot,
  HourBankUsage,
  HourBankAdjustment,
  HourBankReport,
  ServiceWithoutContract,
  ServiceLogReview,
  ServiceLogReviewEntry,
  ServiceLogContractProcess,
  ServiceLogEmployeeBreakdown,
  ServiceLogMonthOverview,
  ServiceLogCustomerSummary,
  WorkRecordListResponse,
  TimerStatusData,
  StartableTask,
  WorkLogKpis,
  ActiveNowEntry,
  RecordSegments,
  TimerStartData,
  TimerStopData,
  ReplyTimerStartData,
  PausedTimerInfo,
  AutoCloseout,
  TimerFinishData,
  TaskRelation,
  TaskRelationType,
  TaskStatusDef,
  ResumableTimer,
  DocumentFolder,
  OteDocument,
  OteDocumentInput,
  DocumentVersion,
  DocumentPermission,
  DocumentStatus,
  ProjectDelivery,
  DeliveryChecklistItem,
  DeliveryStatus,
  Obligation,
  BillingItem,
  ObligationEvent,
  ObligationType,
  ReportCatalogData,
  OrganizationDeleteImpact,
  EmployeeDeleteImpact,
  CustomerHubSummary,
  CustomerWorkSummary,
  InternalHubOverview,
  OrganizationBilling,
  LeadConversionPreview,
  LeadConvertPayload,
  LeadConvertResult,
  ConversionEmailCheck,
  QuoteTransitionAction,
  QuoteConversionOutcome,
  Characterization,
  CharacterizationBanner,
  PortalQuote,
  PortalQuoteDetail,
  Contract,
  ContractDetail,
  ContractInput,
  ContractParty,
  ContractStatus,
  DocumentClassification,
  DocumentLink,
  DocumentLinkEntityType,
  ParentAssignmentCheck,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Bug-report capture: session + request ID wiring
const _bugReportSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
const _lastRequestIds: string[] = [];
/**
 * D-142 / QA-5 #2 — the server's code for "your portal access ended" (a
 * revoked / deactivated grant or an archived contact). One screen for a
 * running session (401) and a sign-in attempt (403): the login page.
 */
export const PORTAL_ACCESS_ENDED = 'PORTAL_ACCESS_ENDED';
export const LOGIN_NOTICE_KEY = 'ote_login_notice';
/** Q-DQ-005 (D-313): any other forced sign-out (expired session / deactivated user) - a soft notice, no reason revealed. */
export const SESSION_ENDED = 'SESSION_ENDED';

const _MAX_REQUEST_IDS = 20;

export function getSessionId(): string { return _bugReportSessionId; }

/** Hand a Blob to the browser as a file download. */
export function saveBlobAs(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/** Parse `Content-Disposition` (RFC 5987 `filename*` first, then `filename`). */
function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}
export function getLastRequestIds(): string[] { return _lastRequestIds.slice(); }

/** Query string from a params object, dropping undefined/null/'' (never sends "undefined"). */
function toQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.append(k, String(v));
  }
  return qs.toString();
}

/**
 * Is this session an admin browsing AS someone (impersonation)? The token the
 * server issued for it carries `impersonated_by`; the banner flag is the
 * second witness. While impersonating, nothing is marked read on that
 * person's behalf (owner, fix round 2026-10-07): the server refuses it too.
 */
export function isImpersonating(): boolean {
  try {
    if (localStorage.getItem('ote_impersonating') === 'true') return true;
    const token = localStorage.getItem('auth_token');
    const part = token?.split('.')[1];
    if (!part) return false;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return !!(JSON.parse(json) as { impersonated_by?: string }).impersonated_by;
  } catch {
    return false;
  }
}

/** The answer of a read receipt that was not sent (impersonation). */
const NOT_SENT: ApiResponse<void> = { success: true } as ApiResponse<void>;

/** P-38a (D-192) — GET /public/meeting-rsvp/:token. */
/** FIX-CLAUDE-B (D-314 §3) — one active connection of an employee to Claude. */
export interface ClaudeConnection {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  kind: 'oauth' | 'personal_key';
  label: string | null;
  client_name: string | null;
  secret_prefix: string | null;
  created_at: string;
  last_used_at: string | null;
}

export interface ClaudeProgressNote {
  id: string;
  note: string;
  created_at: string;
}

export interface PublicMeetingRsvp {
  state: 'valid' | 'updated' | 'cancelled' | 'expired';
  meeting: { title: string; start: string; end: string; duration_minutes: number; meeting_url: string | null; organizer_name: string | null } | null;
  participant: { display_name: string | null; rsvp_status: string } | null;
}

/** P-38b (D-192) — GET /meetings/candidates. */
export interface MeetingCandidates {
  primary_email: { email: string; name: string } | null;
  contacts: { id: string; name: string; email: string | null; has_portal: boolean }[];
  lead: { id: string; name: string; contact_name: string | null; email: string | null } | null;
  staff: { user_id: string; name: string; email: string | null; is_me: boolean }[];
}

/** P-38b (D-192 §6) — GET /meetings/conflicts. */
export interface MeetingConflicts {
  date: string;
  timezone: string;
  has_conflict: boolean;
  items: { meeting_id: string | null; title: string; start: string; end: string; user_ids: string[]; user_names: string[]; is_private: boolean }[];
  next_free_date: string | null;
}

/** P-38b — GET /portal/meetings. */
export interface PortalMeeting {
  id: string;
  title: string;
  meeting_datetime: string;
  duration_minutes: number;
  meeting_url: string | null;
  status: string;
  organization_name: string | null;
  organizer_name: string | null;
  my_participant_id: string | null;
  my_rsvp_status: 'pending' | 'accepted' | 'declined' | 'tentative' | null;
}

/** P-33a (D-185 / D-188) — GET /engagements/internal-work. */
export interface InternalWork {
  organization_id: string;
  organization_name: string;
  engagement_id: string;
  engagement_type: 'development';
  project_id: string | null;
  name: string;
  label: string;
}

/** D-274 §3 — GET /engagements/internal-projects (one row). */
export interface InternalProjectOption {
  project_id: string;
  engagement_id: string;
  engagement_type: string;
  name: string;
  status: string;
  milestones: Array<{ id: string; name: string; parent_milestone_id: string | null; order_index: number | null }>;
}

/** P-47: an explorer "customer" id of the form `lead:<uuid>` is an open lead's folder tree. */
export function leadScopeId(id: string | null | undefined): string | null {
  return id && id.startsWith('lead:') ? id.slice(5) : null;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        // FIX-D300 ס1b (M3): the website can ask the warranty questions (QX-1310 / QX-1307); other clients are not asked.
        'X-OTE-Client': 'web',
      },
      timeout: 30000,
    });

    this.client.interceptors.request.use(
      (config) => {
        // D-279 (QX-ב): another tab signed in as someone else — nothing leaves this tab until it is refreshed.
        if (isAuthSwitchedElsewhere()) return Promise.reject(authSwitchedError(i18n.t('authTabSync.blocked')));
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['X-Session-Id'] = _bugReportSessionId;
        config.headers['X-Request-Id'] = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
            });
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => {
        const reqId = response.headers?.['x-request-id'];
        if (reqId) {
          _lastRequestIds.push(reqId);
          if (_lastRequestIds.length > _MAX_REQUEST_IDS) {
            _lastRequestIds.shift();
          }
        }
        return response.data;
      },
      (error: AxiosError<ApiResponse<any>>) => {
        const reqId = error.response?.headers?.['x-request-id'];
        if (reqId) {
          _lastRequestIds.push(reqId);
          if (_lastRequestIds.length > _MAX_REQUEST_IDS) {
            _lastRequestIds.shift();
          }
        }
        if (error.response?.status === 401) {
          const currentToken = localStorage.getItem('auth_token');
          const reqAuth = error.config?.headers?.Authorization;
          const isStale = currentToken && reqAuth && reqAuth !== `Bearer ${currentToken}`;
          // Never bounce on the public auth screens: /login, and the two
          // password-recovery pages. A 401 there (e.g. a bad reset token) must
          // surface inline, not as a redirect loop back to /login.
          const onPublicAuthScreen =
            window.location.pathname.startsWith('/login') ||
            window.location.pathname.startsWith('/forgot-password') ||
            window.location.pathname.startsWith('/reset-password');
          if (!isStale && !onPublicAuthScreen) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            // D-142 / QA-5 #2: a customer whose portal access ended is signed
            // out with a clear reason — the login screen shows it (the same
            // text a new sign-in attempt gets), never "a temporary error".
            if ((error.response?.data?.error as { error_code?: string } | undefined)?.error_code === PORTAL_ACCESS_ENDED) {
              try { sessionStorage.setItem(LOGIN_NOTICE_KEY, PORTAL_ACCESS_ENDED); } catch { /* storage off: plain redirect */ }
            } else if (currentToken) {
              // Q-DQ-005: there WAS a session and the server ended it - say so softly on the sign-in screen.
              try { sessionStorage.setItem(LOGIN_NOTICE_KEY, SESSION_ENDED); } catch { /* storage off: plain redirect */ }
            }
            window.location.href = '/login';
          }
        }
        // Reject with the API body (callers read `err.error.message`), but keep
        // the axios-style `response.{status,data}` too: many screens read
        // `err.response.data.error.message`, and both shapes must carry the
        // server's human-readable message.
        const body = error.response?.data;
        // FIX-D300-S2 (D-300 sec.2): a 409 CUSTOMER_NO_COVERAGE (only with the switch on) opens the one dialog with
        // "שליחת הודעה למנהל", wherever the work was refused. Every other error: untouched.
        if (error.response?.status === 409 && body && typeof body === 'object') offerCoverageRequest(body);
        if (body && typeof body === 'object') {
          return Promise.reject({
            ...body,
            message: body.error?.message || error.message,
            response: { status: error.response?.status, data: body },
          });
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    return this.client.post('/login', credentials);
  }

  async loginWithGoogle(data: { code: string; redirectUri: string }): Promise<ApiResponse<AuthResponse>> {
    return this.client.post('/auth/google', data);
  }

  async impersonateUser(userId: string): Promise<ApiResponse<AuthResponse & { impersonated_by: { id: string; email: string } }>> {
    return this.client.post(`/impersonate/${userId}`);
  }

  // Password recovery — both are public (no auth header; the token is the
  // credential). The forgot call always resolves success; see the backend.
  async requestPasswordReset(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.client.post('/forgot-password', { email });
  }

  async confirmPasswordReset(token: string, newPassword: string): Promise<ApiResponse<{ message: string }>> {
    return this.client.post('/reset-password', { token, new_password: newPassword });
  }

  // P-38a (D-192 §3) — the invite e-mail's RSVP link (public, no login). GET changes nothing.
  async getPublicMeetingRsvp(token: string): Promise<ApiResponse<PublicMeetingRsvp>> {
    return this.client.get(`/public/meeting-rsvp/${encodeURIComponent(token)}`);
  }

  async respondPublicMeetingRsvp(token: string, response: 'accept' | 'decline'): Promise<ApiResponse<{ rsvp_status: string; responded_at: string }>> {
    return this.client.post(`/public/meeting-rsvp/${encodeURIComponent(token)}`, { response });
  }

  // Tickets
  async getTickets(params?: {
    status?: string;
    active?: boolean;
    /** D-154 (QX-83) — "לא נקראו": only tickets with a message I have not read. */
    unread?: boolean;
    /** FIX-B9 (D-161) — "מחכות לאישור" (tickets / discussions). */
    approval?: 'pending';
    /** FIX-B9 (D-161) — no agent on the ticket ("חדשות שלא שויכו", with status=new). */
    unassigned?: boolean;
    /** FU-62 (P-28b) — "חריגות SLA": only tickets with a breached SLA, filtered by the server. */
    sla?: 'breached';
    priority?: string;
    search?: string;
    customer_id?: string;
    agent_id?: string;
    filter?: string;
    organization_id?: string;
    project_id?: string;
    /** P-02b — the task's own engagement (`t.engagement_id`), or its type(s). */
    engagement_id?: string;
    engagement_type?: string;
    milestone_id?: string;
    task_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get('/tickets', { params });
  }

  async updateTicketPreferences(prefs: {
    assigned?: boolean;
    commented?: boolean;
    created?: boolean;
    include_closed?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.client.patch('/profile/ticket-preferences', prefs);
  }

  async getTicket(id: string): Promise<ApiResponse<Ticket>> {
    return this.client.get(`/tickets/${id}`);
  }

  // FIX-D300-S2 (D-300 sec.3-4) — "שליחת הודעה למנהל" about a customer with no coverage, and the manager's decision.
  async createCoverageRequest(ticketId: string, messageExtra: string | null): Promise<ApiResponse<{ request: CoverageRequestInfo; already: boolean }>> {
    return this.client.post(`/tickets/${ticketId}/coverage-requests`, { message_extra: messageExtra });
  }

  async approveCoverageRequest(ticketId: string, requestId: string, scope: CoverageApprovalScope): Promise<ApiResponse<{ request: CoverageRequestInfo; coverage: TicketCoverageInfo | null }>> {
    return this.client.post(`/tickets/${ticketId}/coverage-requests/${requestId}/approve`, { scope });
  }

  // FIX-D300-S2 — the report "לקוחות בלי כיסוי" (OTE manager) and its quick "בלי חיוב" approval for a whole customer.
  async getUncoveredCustomers(): Promise<ApiResponse<{ block_enabled: boolean; customers: UncoveredCustomer[] }>> {
    return this.client.get('/coverage/uncovered');
  }

  async approveCustomerWithoutBilling(organizationId: string): Promise<ApiResponse<{ id: string }>> {
    return this.client.post(`/coverage/customers/${organizationId}/approve`);
  }

  async createTicket(data: CreateTicketDto): Promise<ApiResponse<Ticket>> {
    return this.client.post('/tickets', data);
  }

  async getAssignableAgents(organizationId?: string): Promise<ApiResponse<Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    is_account_manager?: boolean;
  }>>> {
    const params: any = {};
    if (organizationId) params.organization_id = organizationId;
    return this.client.get('/tickets/meta/assignable-agents', { params });
  }

  async updateTicket(id: string, data: Partial<{
    title: string;
    description: string;
    priority: string;
    assigned_agent_id: string | null;
    status: string;
    due_date: string | null;
    start_date: string | null;
    estimated_hours: number;
    /** P-06/A — the full set of assignees (staff). */
    agent_ids: string[];
    /** P-06/C (D-59) — "כן" to "לשייך גם?". */
    also_assign_parents: boolean;
    /** P-10e (D-82) — the progress bar (0..100, steps of 10). */
    progress_percent: number;
  }>): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${id}`, data);
  }

  async updateTicketStatus(id: string, status: string, cascadeCloseSubtasks?: boolean): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${id}/status`, {
      status,
      ...(cascadeCloseSubtasks !== undefined && { cascade_close_subtasks: cascadeCloseSubtasks }),
    });
  }

  async deleteTicket(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${id}`);
  }

  // §18 — cancellation is a SEPARATE action from status. Sets cancel_reason + cancelled_at.
  async cancelTicket(id: string, reason: string): Promise<ApiResponse<Ticket>> {
    return this.client.post(`/tickets/${id}/cancel`, { reason });
  }

  async uncancelTicket(id: string): Promise<ApiResponse<Ticket>> {
    return this.client.post(`/tickets/${id}/uncancel`, {});
  }

  // B.23 — a completed task is reopened only by this explicit action.
  async reopenTicket(id: string, data?: { reason?: string; status?: string }): Promise<ApiResponse<Ticket>> {
    return this.client.post(`/tickets/${id}/reopen`, data || {});
  }

  // B.17 — manual task relationships
  async getTicketRelations(id: string): Promise<ApiResponse<TaskRelation[]>> {
    return this.client.get(`/tickets/${id}/relations`);
  }

  async addTicketRelation(id: string, toTicketId: string, relationType: TaskRelationType): Promise<ApiResponse<TaskRelation[]>> {
    return this.client.post(`/tickets/${id}/relations`, { to_ticket_id: toTicketId, relation_type: relationType });
  }

  async removeTicketRelation(id: string, relationId: string): Promise<ApiResponse<TaskRelation[]>> {
    return this.client.delete(`/tickets/${id}/relations/${relationId}`);
  }

  async assignTicket(id: string, agentId: string): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${id}/assign`, { agent_id: agentId });
  }

  async getTicketAgents(ticketId: string): Promise<ApiResponse<TicketAgent[]>> {
    return this.client.get(`/tickets/${ticketId}/agents`);
  }

  async addTicketAgent(ticketId: string, agentId: string, alsoAssignParents?: boolean): Promise<ApiResponse<TicketAgent[]>> {
    return this.client.post(`/tickets/${ticketId}/agents`, {
      agent_id: agentId,
      ...(alsoAssignParents ? { also_assign_parents: true } : {}),
    });
  }

  /**
   * P-06/A (D-57, T-16) — "משימה חדשה" in ONE request, files included
   * (multipart). Arrays travel as JSON strings (multer keeps the last value of
   * a repeated field); the server writes the task and its files atomically.
   */
  async createTaskWithFiles(data: Record<string, unknown>, files: File[], idempotencyKey?: string): Promise<ApiResponse<Ticket>> {
    const formData = new FormData();
    for (const [k, v] of Object.entries(data)) {
      // '' is kept for the owner only: it means "no owner on purpose" (D-156 #4).
      if (v === undefined || v === null || (v === '' && k !== 'assigned_agent_id')) continue;
      formData.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
    }
    files.forEach((f) => formData.append('files', f, f.name));
    // P-56 (D-231): one key per attempt — a repeated click / retry never creates a second task.
    return this.client.post('/tickets', formData, { headers: { 'Content-Type': 'multipart/form-data', ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}) } });
  }

  /** P-06/C (D-59) — what "לשייך גם לאבן הדרך ולפרויקט?" names, and whether to ask. */
  async getParentAssignmentCheck(params: { ticketId?: string; milestoneId?: string; projectId?: string; agentIds: string[] }): Promise<ApiResponse<ParentAssignmentCheck>> {
    return this.client.get('/tickets/meta/parent-assignment-check', {
      params: {
        ticket_id: params.ticketId || undefined,
        milestone_id: params.milestoneId || undefined,
        project_id: params.projectId || undefined,
        agent_ids: params.agentIds.join(','),
      },
    });
  }

  /** P-06/B (T-06) — "✓ סיום": "מה בוצע" (required) finishes my open work record and completes the task. */
  async finishTask(id: string, summary: string): Promise<ApiResponse<Ticket>> {
    return this.client.post(`/tickets/${id}/finish`, { summary });
  }

  async removeTicketAgent(ticketId: string, agentId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${ticketId}/agents/${agentId}`);
  }

  // Timeline
  async getTicketTimeline(ticketId: string): Promise<ApiResponse<TicketTimeline>> {
    return this.client.get(`/tickets/${ticketId}/timeline`);
  }

  /** FIX-D251b §3: the customer's people a "שאלה ללקוח" from this task reaches. */
  async getTaskQuestionRecipients(ticketId: string): Promise<ApiResponse<Array<{ id: string; name: string }>>> {
    return this.client.get(`/tickets/${ticketId}/question-recipients`);
  }

  // Comments
  async createComment(ticketId: string, data: CreateCommentDto): Promise<ApiResponse<any>> {
    return this.client.post(`/tickets/${ticketId}/comments`, data);
  }

  /** FIX-P37 — the reply time of a message already sent, once the engagement was chosen (asked once). */
  async recordReplyTime(ticketId: string, data: { seconds: number; engagementId: string; commentId: string }): Promise<ApiResponse<{ logged: boolean; minutes: number }>> {
    return this.client.post(`/tickets/${ticketId}/reply-time`, { reply_seconds: data.seconds, engagement_id: data.engagementId, comment_id: data.commentId });
  }

  async updateComment(ticketId: string, commentId: string, content: string): Promise<ApiResponse<any>> {
    return this.client.patch(`/tickets/${ticketId}/comments/${commentId}`, { content });
  }

  /** D-144 / D-145 — "שליחה לעובד אחר" with an optional internal note. */
  async forwardTicket(ticketId: string, agentId: string, note?: string): Promise<ApiResponse<{ commentId: string | null }>> {
    return this.client.post(`/tickets/${ticketId}/forward`, note ? { agent_id: agentId, note } : { agent_id: agentId });
  }

  /** P-43 (D-213) — people of the chosen customer for "שיוך ללקוח"; the sender's match first. */
  async getAssignCustomerCandidates(ticketId: string, organizationId: string): Promise<ApiResponse<unknown[]>> {
    return this.client.get(`/tickets/${ticketId}/assign-customer/candidates`, { params: { organization_id: organizationId } });
  }

  /** P-43 (D-213..D-215) — "שיוך ללקוח" of a request that arrived with no customer. */
  async assignTicketCustomer(ticketId: string, data: { organization_id: string; customer_id?: string; contact_id?: string }): Promise<ApiResponse<unknown>> {
    return this.client.post(`/tickets/${ticketId}/assign-customer`, data);
  }

  async deleteComment(ticketId: string, commentId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${ticketId}/comments/${commentId}`);
  }

  // Attachments
  async uploadTicketAttachment(
    ticketId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<TicketAttachment>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post(`/tickets/${ticketId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded * 100) / (e.total || 1))),
    });
  }

  async uploadCommentAttachment(
    ticketId: string,
    commentId: string,
    file: File,
    onProgress?: (loaded: number, total: number) => void
  ): Promise<ApiResponse<TicketAttachment>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post(`/tickets/${ticketId}/comments/${commentId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // R-02: the default 30s client timeout cut off big files on a slow line.
      timeout: 300_000,
      ...(onProgress ? { onUploadProgress: (e: { loaded: number; total?: number }) => onProgress(e.loaded, e.total ?? file.size) } : {}),
    });
  }

  async deleteAttachment(ticketId: string, attachmentId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
  }

  // Create ticket with files (multipart)
  async createTicketWithFiles(
    data: CreateTicketDto & { description?: string; priority?: string },
    files: File[]
  ): Promise<ApiResponse<Ticket>> {
    const formData = new FormData();
    formData.append('customer_id', data.customer_id);
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.priority) formData.append('priority', data.priority);
    if (data.assigned_agent_id) formData.append('assigned_agent_id', data.assigned_agent_id);
    if (data.task_type) formData.append('task_type', data.task_type);
    // FIX-CONVPORTAL (D-210): a conversation opened WITH files stays a conversation (the server reads 'true').
    if (data.is_conversation === true) formData.append('is_conversation', 'true');
    if (data.contact_id) formData.append('contact_id', data.contact_id);
    if (data.due_date) formData.append('due_date', data.due_date);
    // The server requires the project (`body('project_id').isUUID()`); without
    // it a ticket with attachments was always refused.
    if (data.project_id) formData.append('project_id', data.project_id);
    // P-36: the customer / engagement / channel travel with the files too (a discussion
    // opened with a customer and files used to lose the customer).
    if (data.organization_id) formData.append('organization_id', data.organization_id);
    if (data.engagement_id) formData.append('engagement_id', data.engagement_id);
    if (data.source) formData.append('source', data.source);
    // Phase K3: multi-participant discussions — multer keeps only the LAST value
    // for repeated text fields, so we JSON-stringify the arrays. The backend
    // parses both array (JSON body) and stringified (multipart) forms.
    if (data.extra_customer_ids && data.extra_customer_ids.length > 0) {
      formData.append('extra_customer_ids', JSON.stringify(data.extra_customer_ids));
    }
    if (data.additional_agent_ids && data.additional_agent_ids.length > 0) {
      formData.append('additional_agent_ids', JSON.stringify(data.additional_agent_ids));
    }
    files.forEach((f) => formData.append('files', f, f.name));
    try {
      return await this.client.post('/tickets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // R-02: files can be big (recordings) - no 30s cut-off, and the percent is shown by the create dialogs.
        timeout: 300_000,
        onUploadProgress: (e: { loaded: number; total?: number }) => {
          uploadProgressBus.emit(e.total ? Math.min(100, Math.round((e.loaded / e.total) * 100)) : null);
        },
      });
    } finally {
      uploadProgressBus.emit(null);
    }
  }

  // Work Logs
  async getWorkLogs(params?: {
    ticket_id?: string;
    agent_id?: string;
    customer_id?: string;
    start_date?: string;
    end_date?: string;
    requires_review?: boolean;
    task_type?: 'ticket' | 'task';
    milestone_id?: string;
    project_id?: string;
    engagement_id?: string;
    start_time_from?: string;
    start_time_to?: string;
    /** P-10d (D-72) — the "פתוחות" tab: segments of open work cycles. */
    open_only?: boolean;
    /** P-24 (D-160) — "לוגים של היום": execution order. */
    sort?: 'start_asc';
    /** SEC-2b (D-92, D-104) — "שלי" / "של אחרים" and the customer (organization) filter. */
    who?: 'mine' | 'others';
    organization_id?: string;
    /** FIX-B9 (D-161, D-168) — the review queue: pending / manual / auto. */
    review?: 'pending' | 'manual' | 'auto' | 'team';
    /** P-37 (D-194 Q-177) — only "מענה לקריאה" logs, or every other log. */
    log_kind?: 'reply' | 'work';
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WorkLog[]>> {
    return this.client.get('/work-logs', { params });
  }

  /** D-319 sec.9 - "פירוט לוגים": the logs of one tab for a month (403 for anyone who sees only their own logs). */
  async getLogDetail(params: { tab: string; month?: string; start_date?: string; end_date?: string; organization_id?: string; project_id?: string; engagement_id?: string; agent_id?: string; limit?: number }): Promise<ApiResponse<WorkLog[]>> {
    return this.client.get('/log-detail', { params });
  }

  async getWorkLog(id: string): Promise<ApiResponse<WorkLog>> {
    return this.client.get(`/work-logs/${id}`);
  }

  async createWorkLog(data: CreateWorkLogDto, idempotencyKey?: string): Promise<ApiResponse<WorkLog>> {
    // DQ-060: the Idempotency-Key of the attempt (optional; a repeated request is answered, not recorded twice).
    return this.client.post('/work-logs', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  /** Create a work log the agent confirmed from a voice draft (links the recording). */
  async createWorkLogFromVoice(data: CreateWorkLogDto & { voice_entry_id: string }): Promise<ApiResponse<WorkLog>> {
    return this.client.post('/work-logs', data);
  }

  // "תיעוד מוצע" (documentation brief 2026-10-12): what the signed-in employee
  // did today without a work log. Computed per request; only dismissals are kept.
  async getWorkLogSuggestions(date?: string): Promise<ApiResponse<WorkLogSuggestionsData>> {
    return this.client.get('/work-logs/suggestions', { params: date ? { date } : undefined });
  }

  // The server re-computes the suggestion by `key` and runs the same checks as
  // POST /work-logs (debt, month lock, hour bank: 409 and so on).
  async acceptWorkLogSuggestion(data: AcceptWorkLogSuggestionDto): Promise<ApiResponse<{ work_log_id?: string }>> {
    return this.client.post('/work-logs/suggestions/accept', data);
  }

  // "לא לתעד": a dismissed suggestion never comes back.
  async dismissWorkLogSuggestion(key: string): Promise<ApiResponse<void>> {
    return this.client.post('/work-logs/suggestions/dismiss', { key });
  }

  // B.19/B.22 — `override_reason` lets an admin edit inside a locked month.
  async updateWorkLog(id: string, data: Partial<CreateWorkLogDto> & { override_reason?: string }): Promise<ApiResponse<WorkLog>> {
    return this.client.patch(`/work-logs/${id}`, data);
  }

  async deleteWorkLog(id: string, overrideReason?: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/work-logs/${id}`, overrideReason ? { data: { override_reason: overrideReason } } : undefined);
  }

  // Work Records (Stage G) — grouped listing endpoint
  /** P-10d (D-79 §5) — the KPI strip of the work-logs page. */
  async getWorkLogKpis(params?: {
    agent_id?: string;
    customer_id?: string;
    project_id?: string;
    engagement_id?: string;
    milestone_id?: string;
    ticket_id?: string;
    /** SEC-2b (D-92, D-104) — "שלי" / "של אחרים" and the customer (organization) filter. */
    who?: 'mine' | 'others';
    organization_id?: string;
  }): Promise<ApiResponse<WorkLogKpis>> {
    return this.client.get('/work-logs/kpis', { params });
  }

  /** P-10d (D-72) — "פעילות עכשיו": every running timer (OTE manager only). */
  async getWorkLogsActiveNow(): Promise<ApiResponse<ActiveNowEntry[]>> {
    return this.client.get('/work-logs/active-now');
  }

  async getWorkRecords(params?: {
    ticket_id?: string;
    agent_id?: string;
    customer_id?: string;
    milestone_id?: string;
    project_id?: string;
    engagement_id?: string;
    status?: 'open' | 'closed';
    requires_review?: boolean;
    start_date?: string;
    end_date?: string;
    /** SEC-2b (D-92, D-104) — "שלי" / "של אחרים" and the customer (organization) filter. */
    who?: 'mine' | 'others';
    organization_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WorkRecordListResponse>> {
    return this.client.get('/work-logs/records', { params });
  }

  // Reassign a work log's owner. Only OTE-admin / customer_manager /
  // project-manager may call; the server enforces this.
  async reassignWorkLog(id: string, agentId: string, confirmParallel?: boolean): Promise<ApiResponse<WorkLog>> {
    // D-299 (QX-1303): the mover answers "did the receiving employee work in parallel?"
    return this.client.patch(`/work-logs/${id}/assignee`, confirmParallel ? { agent_id: agentId, confirm_parallel: true } : { agent_id: agentId });
  }

  // Users
  async getUsers(params?: {
    organization_id?: string;
    role?: string;
    /** תיקון 1 — restrict to canonical active OTE employees
     *  (`employee_profiles.active = TRUE`), the same set the backend accepts
     *  as an account manager. */
    ote_staff?: boolean;
    /** QA-R1 #6 — active logins only (with `ote_staff`: the account-manager rule). */
    active?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<(User & { organization_name?: string })[]>> {
    return this.client.get('/users', { params });
  }

  /**
   * B11: a customer's manager lists the users of their own organization (the
   * server reads the organization from their user row; no parameter names it).
   * Narrow fields only: no email or phone.
   */
  async getMyOrganizationUsers(params?: { limit?: number; offset?: number }): Promise<ApiResponse<Pick<User, 'id' | 'name' | 'role' | 'active' | 'created_at'>[]> & { meta?: { limit: number; offset: number; count: number; active: number } }> {
    return this.client.get('/users/my-organization', { params });
  }

  async createUser(data: {
    organization_id: string;
    role: string;
    name: string;
    email: string;
    password: string;
    phone?: string;
    phone_extension?: string;
  }): Promise<ApiResponse<User>> {
    return this.client.post('/users', data);
  }

  async importUsers(organizationId: string, file: File, defaultRole?: string, defaultPassword?: string): Promise<ApiResponse<{ created: number; skipped: number; total: number; details: any[] }>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId);
    if (defaultRole) formData.append('default_role', defaultRole);
    if (defaultPassword) formData.append('default_password', defaultPassword);
    return this.client.post('/users/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async updateUser(id: string, data: Partial<{
    name: string;
    email: string;
    role: string;
    phone: string;
    phone_extension: string | null;
    active: boolean;
    password: string;
  }>): Promise<ApiResponse<User>> {
    return this.client.patch(`/users/${id}`, data);
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/users/${id}`);
  }

  // Organizations — the Customer List (Repair 4/8).
  //
  // Every parameter below NARROWS the list on the server; none of them widens
  // it. The server decides which customers the caller may see (`customerScopeSql`)
  // before any of these are applied, so filtering and sorting can only reduce
  // that set. `sort` is an opaque key the server resolves against an allowlist.
  async getOrganizations(params?: {
    /** D-274 §1/§2 — a customer PICKER of an OTE employee: + the company ("עבודה פנימית OTE"). */
    include_internal?: 1;
    limit?: number;
    offset?: number;
    search?: string;
    /** 'all' | 'active' | 'inactive' — maps to the canonical `organizations.active`. */
    status?: string;
    account_manager_id?: string;
    /** Inclusive range on the canonical `joined_at`. */
    joined_from?: string;
    joined_to?: string;
    /**
     * Allowlisted sort key: name | joined_at | active | account_manager |
     * last_activity (B11: latest ticket activity, newest first by default,
     * customers without activity last; adds `last_activity_at` to each row).
     */
    sort?: string;
    dir?: 'asc' | 'desc';
    /** P-01/B3 — comma-separated engagement types (active / on hold). */
    engagement_types?: string;
    engagement_active_only?: boolean;
    has_active_project?: boolean;
    has_active_service_contract?: boolean;
    has_active_hour_bank?: boolean;
  }): Promise<ApiResponse<Organization[]>> {
    return this.client.get('/organizations', { params });
  }

  // תיקון 1 — `billing_model` is optional at Customer creation: a new
  // Customer may be created with no billing terms at all (the server stores
  // NULL and never coerces to 'hourly'). `joined_at` is optional too; when
  // omitted the server stamps today for a new Customer.
  async createOrganization(data: {
    name: string;
    billing_model?: string | null;
    hourly_rate?: number;
    retainer_hours?: number;
    account_manager_id?: string | null;
    tax_id?: string;
    email?: string;
    phone?: string;
    phone_secondary?: string;
    joined_at?: string;
    address?: string;
    city?: string;
    website?: string;
    notes?: string;
    /** D-138 / D-141 — "יצירת גישת פורטל" with the new customer. */
    portal_access?: { email: string; password: string };
  }): Promise<ApiResponse<Organization> & { email_sent?: boolean }> {
    return this.client.post('/organizations', data);
  }

  async updateOrganization(id: string, data: Partial<{
    name: string;
    billing_model: string | null;
    hourly_rate: number;
    retainer_hours: number;
    active: boolean;
    account_manager_id: string | null;
    tax_id: string;
    email: string;
    phone: string;
    phone_secondary: string;
    joined_at: string;
    address: string;
    city: string;
    website: string;
    notes: string;
  }>): Promise<ApiResponse<Organization>> {
    return this.client.patch(`/organizations/${id}`, data);
  }

  /**
   * Exceptional hard delete (General Manager only). The server requires a
   * reason (min 5 chars) and the exact customer name as confirmation.
   */
  async deleteOrganization(id: string, body: { reason: string; confirm_name: string }): Promise<ApiResponse<any>> {
    return this.client.delete(`/organizations/${id}`, { data: body });
  }

  async getOrganizationDeleteImpact(id: string): Promise<ApiResponse<OrganizationDeleteImpact>> {
    return this.client.get(`/organizations/${id}/delete-impact`);
  }

  async archiveOrganization(id: string, reason?: string): Promise<ApiResponse<Organization>> {
    return this.client.post(`/organizations/${id}/archive`, reason ? { reason } : {});
  }

  async restoreOrganization(id: string, reason?: string): Promise<ApiResponse<Organization>> {
    return this.client.post(`/organizations/${id}/restore`, reason ? { reason } : {});
  }

  // Dashboard
  async getDashboardSummary(): Promise<ApiResponse<DashboardSummary>> {
    return this.client.get('/dashboard/admin-summary');
  }

  async getBilling(params?: { month?: string; organization_id?: string }): Promise<ApiResponse<BillingSummary[]>> {
    return this.client.get('/dashboard/billing', { params });
  }

  /** D-281: total_amount = "שווי לפי תעריף" (value by rate: billable hours x rate), not the billed revenue. */
  async getHoursByOrg(): Promise<ApiResponse<{ organization_name: string; total_hours: number; total_amount: number }[]>> {
    return this.client.get('/dashboard/hours-by-org');
  }

  async getMonthlyTrend(): Promise<ApiResponse<{ month: string; total_hours: number; billable_hours: number; log_count: number }[]>> {
    return this.client.get('/dashboard/monthly-trend');
  }

  // FIX-CLAUDE-B (D-314 §3, §8, §11) — "חיבור ל-Claude", the "Claude" tag, her progress notes.
  async getClaudeConnections(): Promise<ApiResponse<ClaudeConnection[]>> {
    return this.client.get('/claude/connections');
  }

  async createClaudePersonalKey(label?: string): Promise<ApiResponse<{ id: string; key: string; prefix: string }>> {
    return this.client.post('/claude/personal-keys', { label: label || null });
  }

  async revokeClaudeConnection(id: string): Promise<ApiResponse<{ revoked: boolean }>> {
    return this.client.delete(`/claude/connections/${id}`);
  }

  async getAllClaudeConnections(): Promise<ApiResponse<ClaudeConnection[]>> {
    return this.client.get('/claude/admin/connections');
  }

  async adminRevokeClaudeConnection(id: string): Promise<ApiResponse<{ revoked: boolean }>> {
    return this.client.delete(`/claude/admin/connections/${id}`);
  }

  async getClaudeMarks(ids: string[]): Promise<ApiResponse<{ ids: string[] }>> {
    return this.client.post('/claude/marks', { ids });
  }

  async getClaudeProgressNotes(workRecordId: string): Promise<ApiResponse<ClaudeProgressNote[]>> {
    return this.client.get('/claude/progress-notes', { params: { work_record_id: workRecordId } });
  }

  // Profile
  async getProfile(): Promise<ApiResponse<User & { organization_name: string }>> {
    return this.client.get('/profile');
  }

  async updateProfile(data: {
    name?: string;
    phone?: string;
    current_password?: string;
    new_password?: string;
  }): Promise<ApiResponse<User>> {
    return this.client.patch('/profile', data);
  }

  // Central control audit (audit_log) — General Manager only
  async getAuditTrail(params?: {
    entity_type?: string;
    entity_id?: string;
    actor_id?: string;
    action?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<AuditTrailEntry[]>> {
    return this.client.get('/audit/log', { params });
  }

  async getEntityAuditTrail(type: string, id: string): Promise<ApiResponse<AuditTrailEntry[]>> {
    return this.client.get(`/audit/entity/${encodeURIComponent(type)}/${encodeURIComponent(id)}`);
  }

  // Voice
  async uploadVoice(file: File): Promise<ApiResponse<{ voice_entry: VoiceEntry; work_log?: WorkLog; message: string }>> {
    const formData = new FormData();
    formData.append('audio', file);
    return this.client.post('/voice/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  }

  async getVoiceEntries(params?: { status?: string; limit?: number; offset?: number }): Promise<ApiResponse<VoiceEntry[]>> {
    return this.client.get('/voice', { params });
  }

  /** D-234 (FIX-B36) - is the AI (transcription + parsing) connected? Never returns a key. */
  async getVoiceStatus(): Promise<ApiResponse<{ configured: boolean; transcription: boolean; parsing: boolean }>> {
    return this.client.get('/voice/status');
  }

  // Projects
  async getProjects(params?: {
    organization_id?: string;
    status?: string;
    /** P-02b — the server lists projects and developments only (default: both). */
    type?: 'project' | 'development';
    limit?: number;
    offset?: number;
    /** P-02a — also list archived projects. */
    include_archived?: boolean;
  }): Promise<ApiResponse<(Project & { ticket_count: number; open_ticket_count: number; total_actual_hours: number; organization_name: string; overdue_ticket_count: number })[]>> {
    return this.client.get('/projects', { params });
  }

  async getProject(id: string): Promise<ApiResponse<Project & { milestones: (Milestone & { tickets_total: number; tickets_closed: number; total_actual_hours: number })[]; tasks: (Ticket & { milestones: Milestone[]; milestone_count: number })[]; stats: ProjectStats; organization_name: string; project_manager: { user_id: string; name: string | null } | null; team_lead: { user_id: string; name: string | null } | null; financials: ProjectFinancials | null; characterization?: CharacterizationBanner | null; permissions?: { can_edit: boolean; can_edit_commercial: boolean; can_view_financials: boolean; can_create_milestone?: boolean; can_edit_milestone?: boolean; can_delete_milestone?: boolean; can_bulk_add?: boolean; can_delete?: boolean; can_delete_task?: boolean } }>> {
    return this.client.get(`/projects/${id}`);
  }

  /** P-04/B (D-31) — a project or a development, from the project form. */
  async createProject(data: {
    organization_id: string;
    type?: 'project' | 'development';
    name: string;
    project_manager_id: string;
    team_lead_id?: string | null;
    description?: string | null;
    start_date: string;
    end_date: string;
    planned_hours_mode?: 'undefined' | 'automatic' | 'manual';
    planned_hours_manual?: number | null;
    fixed_price?: number | null;
    server_monthly_fee?: number | null;
    server_monthly_cost?: number | null;
    payment_terms?: string | null;
    /** P-07 (D-74 §8) — opened from an "אפיון" card: linked in the same transaction. */
    characterization_id?: string;
  }): Promise<ApiResponse<Project & { engagement_id: string }>> {
    return this.client.post('/projects', data);
  }

  /** P-04/C — the "שעות" tab. */
  async getProjectHours(id: string): Promise<ApiResponse<ProjectHours>> {
    return this.client.get(`/projects/${id}/hours`);
  }

  /** P-04/C — the "צוות" tab. */
  async getProjectTeam(id: string): Promise<ApiResponse<ProjectTeamPerson[]>> {
    return this.client.get(`/projects/${id}/team`);
  }

  async updateProject(id: string, data: Partial<Project> & { cancellation_reason?: string; warranty_until?: string | null; no_warranty?: boolean }): Promise<ApiResponse<Project>> {
    return announceBillingWarnings(await this.client.patch(`/projects/${id}`, data), data.status === 'cancelled' ? 'cancel' : 'price');
  }

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/projects/${id}`);
  }

  // P-02a/C3 — a cancelled project with data is archived instead of deleted.
  async archiveProject(id: string): Promise<ApiResponse<Project>> {
    return this.client.post(`/projects/${id}/archive`);
  }

  async restoreProject(id: string): Promise<ApiResponse<Project>> {
    return this.client.post(`/projects/${id}/restore`);
  }

  async getProjectTickets(projectId: string, params?: {
    status?: string; limit?: number; offset?: number;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/projects/${projectId}/tickets`, { params });
  }

  // Stage D2 — project-scoped assignments (PMs + team members)
  async getProjectAssignments(projectId: string): Promise<ApiResponse<ProjectAssignments>> {
    return this.client.get(`/projects/${projectId}/assignments`);
  }

  async assignProjectManager(projectId: string, userId: string): Promise<ApiResponse<ProjectManagerAssignment>> {
    return this.client.post(`/projects/${projectId}/managers`, { user_id: userId });
  }

  async removeProjectManager(projectId: string, userId: string): Promise<ApiResponse<{ removed: boolean }>> {
    return this.client.delete(`/projects/${projectId}/managers/${userId}`);
  }

  async addProjectMember(projectId: string, userId: string, role: 'member' = 'member'): Promise<ApiResponse<ProjectTeamMember>> {
    return this.client.post(`/projects/${projectId}/members`, { user_id: userId, role });
  }

  async updateProjectMemberRole(projectId: string, userId: string, role: 'team_lead' | 'member'): Promise<ApiResponse<ProjectTeamMember>> {
    return this.client.patch(`/projects/${projectId}/members/${userId}`, { role });
  }

  async removeProjectMember(projectId: string, userId: string): Promise<ApiResponse<{ removed: boolean }>> {
    return this.client.delete(`/projects/${projectId}/members/${userId}`);
  }

  // Milestones
  async getMilestones(projectId: string): Promise<ApiResponse<(Milestone & { tickets_total: number; tickets_closed: number; total_estimated_hours: number; total_actual_hours: number })[]>> {
    return this.client.get(`/projects/${projectId}/milestones`);
  }

  /** D-280 — the "אבן דרך" picker of the new-task form: every milestone of the project (core first). */
  async getMilestoneTaskChoices(projectId: string): Promise<ApiResponse<Array<{ id: string; name: string; parent_milestone_id: string | null; archived_at: string | null; is_core: boolean }>>> {
    return this.client.get('/milestones/task-choices', { params: { project_id: projectId } });
  }

  // A milestone is created under its project (project → milestone → task); a
  // task is linked to it from the task side (`moveTicket`), never by ticket_id here.
  async createMilestone(projectId: string, data: MilestoneFormBody): Promise<ApiResponse<Milestone>> {
    return this.client.post(`/projects/${projectId}/milestones`, data);
  }

  /** P-05/F — "הוספת אבני דרך ומשימות": the whole tree in one transaction. */
  async bulkAddMilestonesAndTasks(projectId: string, data: BulkAddBody): Promise<ApiResponse<{
    milestones: { id: string; name: string; created: boolean; parent_milestone_id: string | null }[];
    tasks: { id: string; title: string; milestone_id: string | null }[];
  }>> {
    return this.client.post(`/projects/${projectId}/bulk-add`, data);
  }

  async getAllMilestones(params?: {
    limit?: number;
    offset?: number;
    search?: string;
    project_id?: string;
    organization_id?: string;
    customer_id?: string;
    worker_id?: string;
    status?: string;
    /** P-02c/F-05 — only milestones that are not archived (the pickers that log work). */
    exclude_archived?: boolean;
    /** P-05/H — the responsible, a date range, late, completed. */
    responsible_id?: string;
    date_from?: string;
    date_to?: string;
    overdue?: boolean;
    completed?: boolean;
  }): Promise<ApiResponse<(Milestone & {
    project_name: string;
    project_kind?: string;
    organization_id: string;
    organization_name: string;
    customer_id?: string;
    customer_name?: string;
    ticket_title?: string;
    tickets_total: number;
    tickets_closed: number;
  })[]> & { permissions?: { can_create: boolean } }> {
    return this.client.get('/milestones/all', { params });
  }

  async getMilestone(id: string): Promise<ApiResponse<Milestone & {
    project_name: string;
    project_kind?: string;
    organization_name: string;
    organization_id: string;
    deadline_warning_days: number;
    milestone_assignees: MyMilestoneAssignee[];
    sibling_milestones: SiblingMilestone[];
    sub_milestones: (Milestone & { metrics: MilestoneMetrics | null })[];
    metrics: MilestoneMetrics | null;
    permissions: MilestonePermissions;
  }>> {
    return this.client.get(`/milestones/${id}`);
  }

  /** P-05/D — the milestone page tabs. */
  async getMilestoneHours(id: string): Promise<ApiResponse<{
    planned_hours_mode: 'undefined' | 'automatic' | 'manual';
    planned_hours: number | null;
    actual_hours: number;
    by_employee: { user_id: string; name: string; estimated_hours: number; actual_hours: number }[];
  }>> {
    return this.client.get(`/milestones/${id}/hours`);
  }

  async getMilestoneTeam(id: string): Promise<ApiResponse<{ user_id: string; name: string; roles: ('responsible' | 'assignee' | 'task_assignee')[]; hours: number }[]>> {
    return this.client.get(`/milestones/${id}/team`);
  }

  async getMilestoneHistory(id: string): Promise<ApiResponse<{
    id: string; action: string; old_values: Record<string, unknown> | null; new_values: Record<string, unknown> | null;
    created_at: string; actor_id: string | null; actor_type: string; actor_name: string | null;
  }[]>> {
    return this.client.get(`/milestones/${id}/history`);
  }

  async updateMilestone(id: string, data: Partial<Milestone> & { assignee_ids?: string[] }): Promise<ApiResponse<Milestone>> {
    return this.client.patch(`/milestones/${id}`, data);
  }

  // B.11 — milestone under a project OR a development
  async createParentedMilestone(data: MilestoneFormBody & {
    project_id?: string;
    development_id?: string;
  }): Promise<ApiResponse<Milestone>> {
    return this.client.post('/milestones', data);
  }

  // B.23 — completion returns the still-open child tasks (no cascade).
  async completeMilestone(id: string, notify: 'none' | 'admin' | 'customer' = 'none'): Promise<ApiResponse<Milestone & { open_children?: { id: string; title: string; status: string }[] }>> {
    return this.client.patch(`/milestones/${id}/complete`, { notify });
  }

  async deleteMilestone(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/milestones/${id}`);
  }

  // P-02a/C2 — a milestone with work is archived instead of deleted.
  async archiveMilestone(id: string): Promise<ApiResponse<Milestone>> {
    return this.client.post(`/milestones/${id}/archive`);
  }

  async restoreMilestone(id: string): Promise<ApiResponse<Milestone>> {
    return this.client.post(`/milestones/${id}/restore`);
  }

  async reorderMilestones(projectId: string, items: { id: string; order_index: number }[]): Promise<ApiResponse<void>> {
    return this.client.patch(`/projects/${projectId}/milestones/reorder`, items);
  }

  async getMilestoneTickets(milestoneId: string, params?: {
    limit?: number; offset?: number;
    /** P-05/D — also the tasks of its sub-milestones (each row carries milestone_id). */
    include_sub_milestones?: boolean;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/milestones/${milestoneId}/tickets`, { params });
  }

  // Organization details & tickets
  async getOrganization(id: string): Promise<ApiResponse<Organization & { project_count: number; ticket_count: number; user_count: number }>> {
    return this.client.get(`/organizations/${id}`);
  }

  async getOrganizationTickets(orgId: string, params?: {
    project_id?: string;
    status?: string;
    direct_only?: string;
    /** P-02b — `task|ticket|discussion` (comma list) and the task's own engagement. */
    task_type?: string;
    engagement_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/organizations/${orgId}/tickets`, { params });
  }

  // Ticket hierarchy
  async moveTicket(ticketId: string, data: {
    project_id?: string | null;
    milestone_id?: string | null;
    hours_action?: 'move' | 'keep' | 'split';
  }): Promise<ApiResponse<Ticket>> {
    return this.client.patch(`/tickets/${ticketId}/move`, data);
  }

  async getSubtasks(ticketId: string): Promise<ApiResponse<Ticket[]>> {
    return this.client.get(`/tickets/${ticketId}/subtasks`);
  }

  async createSubtask(ticketId: string, data: {
    title: string;
    description?: string;
    priority?: string;
    estimated_hours?: number;
    assigned_agent_id?: string;
  }): Promise<ApiResponse<Ticket>> {
    return this.client.post(`/tickets/${ticketId}/subtasks`, data);
  }

  // Notifications
  /** No params: the bell's unread list (unchanged). With params: the drawer feed (B1); `q`/`types`: its server search (P-30b). */
  async getNotifications(params?: { status?: 'all' | 'unread'; limit?: number; before?: string; q?: string; types?: string; organization_id?: string }): Promise<ApiResponse<Notification[]> & { meta?: { status: 'all' | 'unread'; limit: number; has_more: boolean } }> {
    return params ? this.client.get('/notifications', { params }) : this.client.get('/notifications');
  }

  async markNotificationRead(id: string): Promise<ApiResponse<void>> {
    if (isImpersonating()) return NOT_SENT;
    return this.client.patch(`/notifications/${id}/read`);
  }

  async getNotificationPreferences(): Promise<ApiResponse<NotificationPreferencesResponse>> {
    return this.client.get('/notifications/preferences');
  }

  async updateNotificationPreferences(preferences: NotificationPreferenceUpdate[]): Promise<ApiResponse<{ updated: number }>> {
    return this.client.put('/notifications/preferences', { preferences });
  }

  // W-01 - "התראה חדשה" from the bell (personal reminders). The server decides who may be chosen.
  async getReminderRecipients(): Promise<ApiResponse<Array<{ id: string; name: string; self: boolean }>>> {
    return this.client.get('/reminders/recipients');
  }

  async getMyReminders(): Promise<ApiResponse<ReminderRow[]>> {
    return this.client.get('/reminders');
  }

  async createReminder(body: ReturnType<typeof reminderPayload>): Promise<ApiResponse<ReminderRow>> {
    if (isImpersonating()) return NOT_SENT as unknown as ApiResponse<ReminderRow>;
    return this.client.post('/reminders', body);
  }

  async updateReminder(id: string, body: ReturnType<typeof reminderPayload>): Promise<ApiResponse<ReminderRow>> {
    if (isImpersonating()) return NOT_SENT as unknown as ApiResponse<ReminderRow>;
    return this.client.patch(`/reminders/${id}`, body);
  }

  async deleteReminder(id: string): Promise<ApiResponse<void>> {
    if (isImpersonating()) return NOT_SENT;
    return this.client.delete(`/reminders/${id}`);
  }

  async ackReminderOccurrence(occurrenceId: string, kind: 'done' | 'silence'): Promise<ApiResponse<void>> {
    if (isImpersonating()) return NOT_SENT;
    return this.client.post(`/reminders/occurrences/${occurrenceId}/${kind}`);
  }

  async markAllNotificationsRead(): Promise<ApiResponse<void>> {
    if (isImpersonating()) return NOT_SENT;
    return this.client.post('/notifications/read-all');
  }

  /** P-39 (D-199): "סמן כלא נקרא" - personal; opening the ticket returns it to read. */
  async markTicketUnread(ticketId: string): Promise<ApiResponse<void>> {
    if (isImpersonating()) return NOT_SENT;
    return this.client.post(`/tickets/${ticketId}/mark-unread`);
  }

  async markTicketChatRead(ticketId: string): Promise<ApiResponse<void>> {
    if (isImpersonating()) return NOT_SENT;
    return this.client.post(`/tickets/${ticketId}/chat/read`);
  }

  // Timer (Stage G, B.18 multiple timers)
  // Starts a timer on a task (several tasks may run at once), creating the open
  // Work Record if none exists yet. 409 if this task's timer already runs.
  async startTimer(ticketId: string, engagementId?: string): Promise<ApiResponse<TimerStartData>> {
    // D-146 (Q-98) — a ticket without an engagement: the one the employee chose (409 ENGAGEMENT_REQUIRED otherwise).
    return this.client.post('/timer/start', engagementId ? { ticket_id: ticketId, engagement_id: engagementId } : { ticket_id: ticketId });
  }

  // P-37 (D-194) — "אני על זה" on a ticket / discussion (pauses the running task
  // timers unless `parallel`), "גמרתי", and "שניהם במקביל".
  async startReplyTimer(ticketId: string, opts: { engagementId?: string; parallel?: boolean } = {}): Promise<ApiResponse<ReplyTimerStartData>> {
    return this.client.post('/timer/reply/start', {
      ticket_id: ticketId,
      ...(opts.engagementId ? { engagement_id: opts.engagementId } : {}),
      ...(opts.parallel ? { parallel: true } : {}),
    });
  }

  async finishReplyTimer(ticketId: string): Promise<ApiResponse<TimerStopData>> {
    return this.client.post('/timer/reply/done', { ticket_id: ticketId });
  }

  async resumePausedTimers(replyTimerId: string): Promise<ApiResponse<{ resumed: PausedTimerInfo[] }>> {
    return this.client.post('/timer/reply/resume-paused', { timer_id: replyTimerId });
  }

  // Stops ONE timer (by timer_id; the legacy no-arg call works when exactly one
  // runs), persisting its segment. The Work Record stays OPEN.
  async stopTimer(timerId?: string, summary?: string, opts: { resumePaused?: boolean; noResumeOffer?: boolean } = {}): Promise<ApiResponse<TimerStopData>> {
    // P-10e (D-85) — the clock-out window stops a checked timer with its "מה עשיתי".
    const body: Record<string, string | boolean> = {};
    if (timerId) body.timer_id = timerId;
    if (summary && summary.trim()) body.summary = summary.trim();
    // FIX-P37 — "עצירה ומעבר" from "אני על זה": the task it paused stays paused.
    if (opts.resumePaused === false) body.resume_paused = false;
    if (opts.noResumeOffer) body.no_resume_offer = true;
    const res: ApiResponse<TimerStopData> = await this.client.post('/timer/stop', body);
    // FIX-B17 (D-198 QX-147) — a task left paused by "אני על זה" is offered now.
    offerPausedResume(res?.data?.resume_offer);
    return res;
  }

  // "Finish + document + close" — closes an open Work Record. REQUIRES a summary
  // (min 5 chars). The targeted timer (if running) is auto-closed first.
  async finishTimer(
    summary: string,
    target?: { timerId?: string; workRecordId?: string }
  ): Promise<ApiResponse<TimerFinishData>> {
    const res: ApiResponse<TimerFinishData> = await this.client.post('/timer/finish', {
      summary,
      ...(target?.timerId ? { timer_id: target.timerId } : {}),
      ...(target?.workRecordId ? { work_record_id: target.workRecordId } : {}),
    });
    // FIX-B17 (D-198 QX-147) — a task left paused by "אני על זה" is offered now.
    offerPausedResume(res?.data?.resume_offer);
    return res;
  }

  // FIX-TIMER-START-EDIT — change the start time of a running / paused timer.
  async editTimerStart(timerId: string, startedAt: string): Promise<ApiResponse<{ old_started_at: string; new_started_at: string }>> {
    return this.client.patch(`/timer/${timerId}/start`, { started_at: startedAt });
  }

  async getTimerStatus(): Promise<ApiResponse<TimerStatusData>> {
    return this.client.get('/timer/status');
  }

  // P-10a (D-67) — my open tasks for the "התחל עבודה" window.
  async getStartableTasks(params?: {
    organization_id?: string; search?: string;
    /** P-10d — the manual work-log picker narrows by the task's place too. */
    project_id?: string; engagement_id?: string; milestone_id?: string;
    /** P-30c — the manual work-log picker also lists my completed tasks. */
    include_completed?: boolean;
  }): Promise<ApiResponse<StartableTask[]>> {
    return this.client.get('/timer/tasks', { params });
  }

  /** D-275 (Q2) — the internal discussions I take part in and may log time on. */
  async getInternalDiscussions(params?: { search?: string }): Promise<ApiResponse<StartableTask[]>> {
    return this.client.get('/timer/internal-discussions', { params });
  }

  // P-10a (D-64) — every stop of one of my work records (the ✓ window).
  async getRecordSegments(workRecordId: string): Promise<ApiResponse<RecordSegments>> {
    return this.client.get(`/timer/records/${workRecordId}/segments`);
  }

  // B.13 — controlled task status list
  async getTaskStatuses(): Promise<ApiResponse<TaskStatusDef[]>> {
    return this.client.get('/task-statuses');
  }

  // Export URLs
  getExportWorkLogsUrl(params?: { start_date?: string; end_date?: string; organization_id?: string; customer_id?: string; engagement_id?: string; agent_id?: string; project_id?: string; format?: 'csv' | 'pdf'; group_by?: string; tab?: string; include_non_billable?: boolean }): string {
    const sp = new URLSearchParams();
    if (params?.start_date) sp.set('start_date', params.start_date);
    if (params?.end_date) sp.set('end_date', params.end_date);
    if (params?.organization_id) sp.set('organization_id', params.organization_id);
    if (params?.customer_id) sp.set('customer_id', params.customer_id);
    if (params?.engagement_id) sp.set('engagement_id', params.engagement_id);
    if (params?.agent_id) sp.set('agent_id', params.agent_id);
    if (params?.project_id) sp.set('project_id', params.project_id);
    if (params?.format) sp.set('format', params.format);
    if (params?.tab) sp.set('tab', params.tab);
    if (params?.include_non_billable) sp.set('include_non_billable', 'true');
    if (params?.group_by && params.group_by !== 'none') sp.set('group_by', params.group_by);
    return `${API_URL}/export/work-logs?${sp.toString()}`;
  }

  getExportBillingUrl(params?: { month?: string }): string {
    const sp = new URLSearchParams();
    if (params?.month) sp.set('month', params.month);
    return `${API_URL}/export/billing?${sp.toString()}`;
  }

  // --- API Keys ---
  async getApiKeys(): Promise<ApiResponse<ApiKeyInfo[]>> {
    return this.client.get('/settings/api-keys');
  }

  async createApiKey(data: {
    name: string;
    scopes: string[];
    scope_type?: string;
    scoped_org_id?: string;
    organization_id?: string;
  }, idempotencyKey?: string): Promise<ApiResponse<ApiKeyInfo>> {
    // DQ-354: the Idempotency-Key of the attempt - a double click never creates two keys.
    return this.client.post('/settings/api-keys', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  async revokeApiKey(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/api-keys/${id}`);
  }

  // --- Webhooks ---
  async getWebhooks(): Promise<ApiResponse<WebhookEndpoint[]>> {
    return this.client.get('/settings/webhooks');
  }

  async createWebhook(data: {
    url: string;
    events: string[];
    organization_id?: string;
  }): Promise<ApiResponse<WebhookEndpoint & { secret: string }>> {
    return this.client.post('/settings/webhooks', data);
  }

  async updateWebhook(id: string, data: Partial<{
    url: string;
    events: string[];
    active: boolean;
  }>): Promise<ApiResponse<WebhookEndpoint>> {
    return this.client.patch(`/settings/webhooks/${id}`, data);
  }

  async deleteWebhook(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/webhooks/${id}`);
  }

  async getWebhookDeliveries(webhookId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WebhookDelivery[]>> {
    return this.client.get(`/settings/webhooks/${webhookId}/deliveries`, { params });
  }

  async testWebhook(webhookId: string): Promise<ApiResponse<WebhookDelivery>> {
    return this.client.post(`/settings/webhooks/${webhookId}/test`);
  }

  // --- Search ---
  async search(params: {
    q: string;
    types?: string;
    org?: string;
    from?: string;
    to?: string;
    agent?: string;
  }): Promise<ApiResponse<SearchResponse>> {
    return this.client.get('/search', { params });
  }

  // --- Reports ---
  async getProjectBurndown(projectId: string, params?: {
    from?: string;
    to?: string;
  }): Promise<ApiResponse<BurndownDataPoint[]>> {
    return this.client.get(`/projects/${projectId}/burndown`, { params });
  }

  async getProjectBudgetActual(projectId: string): Promise<ApiResponse<BudgetVsActualData>> {
    return this.client.get(`/projects/${projectId}/budget-actual`);
  }

  async getMonthlyRevenue(filters?: ReportFilterValues): Promise<ApiResponse<MonthlyRevenuePoint[]>> {
    return this.client.get('/reports/monthly-revenue', { params: filters });
  }

  async getHoursByAgent(month?: string, filters?: ReportFilterValues): Promise<ApiResponse<HoursByAgentData[]>> {
    return this.client.get('/reports/hours-by-agent', { params: { ...(month ? { month } : {}), ...(filters || {}) } });
  }

  async getTicketsByPriority(filters?: ReportFilterValues): Promise<ApiResponse<TicketsByPriorityData[]>> {
    return this.client.get('/reports/tickets-by-priority', { params: filters });
  }

  async getHoursByTicket(orgId?: string, filters?: ReportFilterValues): Promise<ApiResponse<HoursByTicketData[]>> {
    return this.client.get('/reports/hours-by-ticket', { params: { ...(orgId ? { org_id: orgId } : {}), ...(filters || {}) } });
  }

  async getAgentSummary(range?: { from?: string; to?: string }): Promise<ApiResponse<AgentSummaryData>> {
    return this.client.get('/reports/agent-summary', { params: range });
  }

  // --- §58 Profitability (Stage טו) — admin/internal, deny-by-default ---
  getProfitability(level: string, params?: { org_id?: string; month?: string; from?: string; to?: string; range?: 'all'; project_id?: string; engagement_id?: string }): Promise<ApiResponse<ProfitabilityData>> {
    return this.client.get(`/profitability/${level}`, { params });
  }

  // P-28b (D-169) - the project's "רווחיות" tab, the customer summary, the business report (P-28a contract).
  getProjectProfit(projectId: string, params?: ProfitPeriodParams): Promise<ApiResponse<ProjectProfit>> {
    return this.client.get(`/profitability/project/${projectId}`, { params });
  }

  getCustomerProfit(orgId: string, params?: ProfitPeriodParams): Promise<ApiResponse<CustomerProfit>> {
    return this.client.get(`/profitability/customer/${orgId}`, { params });
  }

  getBusinessProfit(month?: string): Promise<ApiResponse<BusinessProfit>> {
    return this.client.get('/profitability/business', { params: month ? { month } : undefined });
  }

  // P-34 (D-184, D-188) - every server active in the month, all engagement types (the OTE manager only).
  getServersReport(month?: string): Promise<ApiResponse<ServersReport>> {
    return this.client.get('/profitability/servers', { params: month ? { month } : undefined });
  }

  // P-25b (D-164) — "העובד המוביל": OTE manager only (403 for anyone else).
  getTopEmployee(period: 'day' | 'month', date?: string): Promise<ApiResponse<TopEmployeesData>> {
    return this.client.get('/profitability/top-employee', { params: { period, date } });
  }

  // --- §56 Reports catalog + generic export (Stage יד) ---
  getReportExportUrl(key: string, format: 'csv' | 'pdf', params?: ReportFilterValues & { month?: string }): string {
    const sp = new URLSearchParams({ format });
    for (const [k, v] of Object.entries(params || {})) {
      if (v) sp.set(k, String(v));
    }
    return `${API_URL}/reports/export/${encodeURIComponent(key)}?${sp.toString()}`;
  }

  /** Download a report in the given format (Bearer token → blob → anchor). */
  async downloadReport(key: string, format: 'csv' | 'pdf', params?: ReportFilterValues & { month?: string }): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const url = this.getReportExportUrl(key, format, params);
    const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    if (!response.ok) throw new Error(`Export failed: ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    // DQ-325: the server's name (it carries the range); else the Israeli day, not the UTC day.
    a.download = fileNameFromDisposition(response.headers.get('content-disposition')) || `${key}-${israelToday()}.${format}`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  }

  /** §56 — categorized report registry the ReportsPage tabs are driven from. */
  async getReportCatalog(lang?: 'he' | 'en'): Promise<ApiResponse<ReportCatalogData>> {
    return this.client.get('/reports/catalog', { params: lang ? { lang } : undefined });
  }

  /** Interactive, filterable report view — same query/scope as the export. */
  async runReport(key: string, filters?: ReportFilterValues): Promise<ApiResponse<ReportRunResult>> {
    return this.client.get(`/reports/run/${encodeURIComponent(key)}`, { params: filters });
  }

  // --- Language ---
  async updateLanguage(language: string): Promise<ApiResponse<User>> {
    return this.client.patch('/profile', { preferred_language: language });
  }

  // --- Integration Presets ---
  async getIntegrationPresets(): Promise<ApiResponse<IntegrationPreset[]>> {
    return this.client.get('/integrations/presets');
  }

  // --- Integration Instances ---
  async getIntegrations(): Promise<ApiResponse<IntegrationInstance[]>> {
    return this.client.get('/integrations');
  }

  async getIntegration(id: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.get(`/integrations/${id}`);
  }

  async createIntegration(data: {
    key: string;
    name: string;
    provider_type: string;
    preset_key?: string;
    source_type: string;
    source_config?: Record<string, any>;
    notes?: string;
  }): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post('/integrations', data);
  }

  async updateIntegration(id: string, data: Partial<{
    name: string;
    source_config: Record<string, any>;
    enabled: boolean;
    notes: string;
  }>): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.patch(`/integrations/${id}`, data);
  }

  async deleteIntegration(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${id}`);
  }

  // --- Field Mappings ---
  async getIntegrationMappings(instanceId: string): Promise<ApiResponse<IntegrationFieldMapping[]>> {
    return this.client.get(`/integrations/${instanceId}/mappings`);
  }

  async createIntegrationMapping(instanceId: string, data: {
    source_field_key: string;
    target_field_key: string;
    jsonpath_expression: string;
    is_required?: boolean;
    default_value?: string;
  }): Promise<ApiResponse<IntegrationFieldMapping>> {
    return this.client.post(`/integrations/${instanceId}/mappings`, data);
  }

  async deleteIntegrationMapping(instanceId: string, mappingId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/mappings/${mappingId}`);
  }

  // --- Pipeline Steps ---
  async getIntegrationSteps(instanceId: string): Promise<ApiResponse<IntegrationPipelineStep[]>> {
    return this.client.get(`/integrations/${instanceId}/steps`);
  }

  async createIntegrationStep(instanceId: string, data: {
    step_order: number;
    step_type: string;
    config_json?: Record<string, any>;
    enabled?: boolean;
  }): Promise<ApiResponse<IntegrationPipelineStep>> {
    return this.client.post(`/integrations/${instanceId}/steps`, data);
  }

  async updateIntegrationStep(instanceId: string, stepId: string, data: Partial<{
    step_order: number;
    config_json: Record<string, any>;
    enabled: boolean;
  }>): Promise<ApiResponse<IntegrationPipelineStep>> {
    return this.client.patch(`/integrations/${instanceId}/steps/${stepId}`, data);
  }

  async swapIntegrationStepOrder(instanceId: string, stepIdA: string, stepIdB: string): Promise<ApiResponse<IntegrationPipelineStep[]>> {
    return this.client.post(`/integrations/${instanceId}/steps/swap`, { stepIdA, stepIdB });
  }

  async deleteIntegrationStep(instanceId: string, stepId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/steps/${stepId}`);
  }

  // --- Targets ---
  async getIntegrationTargets(instanceId: string): Promise<ApiResponse<IntegrationTarget[]>> {
    return this.client.get(`/integrations/${instanceId}/targets`);
  }

  async createIntegrationTarget(instanceId: string, data: {
    target_order: number;
    target_type: string;
    field_bindings?: Record<string, any>;
    enabled?: boolean;
  }): Promise<ApiResponse<IntegrationTarget>> {
    return this.client.post(`/integrations/${instanceId}/targets`, data);
  }

  async updateIntegrationTarget(instanceId: string, targetId: string, data: Partial<{
    target_order: number;
    target_type: string;
    field_bindings: Record<string, any>;
    enabled: boolean;
  }>): Promise<ApiResponse<IntegrationTarget>> {
    return this.client.patch(`/integrations/${instanceId}/targets/${targetId}`, data);
  }

  async deleteIntegrationTarget(instanceId: string, targetId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/targets/${targetId}`);
  }

  // --- Secrets ---
  async setIntegrationSecret(instanceId: string, key: string, value: string): Promise<ApiResponse<void>> {
    return this.client.put(`/integrations/${instanceId}/secrets`, { key_name: key, secret_value: value });
  }

  async deleteIntegrationSecret(instanceId: string, key: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/secrets/${key}`);
  }

  async getIntegrationSecretKeys(instanceId: string): Promise<ApiResponse<IntegrationSecretKey[]>> {
    return this.client.get(`/integrations/${instanceId}/secrets`);
  }

  // --- Integration Logs ---
  async getIntegrationLogs(instanceId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<IntegrationInboundLog[]>> {
    return this.client.get(`/integrations/${instanceId}/logs`, { params });
  }

  async getIntegrationLogDetail(logId: string): Promise<ApiResponse<IntegrationInboundLog>> {
    // Backend: GET /integrations/logs/:logId (log ids are global, not nested under the instance).
    return this.client.get(`/integrations/logs/${logId}`);
  }

  // --- Test Pipeline ---
  async testIntegrationPipeline(instanceId: string, payload: Record<string, any>): Promise<ApiResponse<TestPipelineResponse>> {
    return this.client.post(`/integrations/${instanceId}/test`, { payload });
  }

  // --- Integration Approval Workflow ---
  async submitIntegration(instanceId: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post(`/integrations/${instanceId}/submit`);
  }

  async approveIntegration(instanceId: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post(`/integrations/${instanceId}/approve`);
  }

  async rejectIntegration(instanceId: string, rejection_reason?: string): Promise<ApiResponse<IntegrationInstance>> {
    return this.client.post(`/integrations/${instanceId}/reject`, { rejection_reason });
  }

  // --- Gmail Add-on API key ---
  async getIntegrationAddonKey(instanceId: string): Promise<ApiResponse<{
    has_key: boolean;
    prefix: string | null;
    created_at: string | null;
    last_used_at: string | null;
  }>> {
    return this.client.get(`/integrations/${instanceId}/addon-key`);
  }

  async createIntegrationAddonKey(instanceId: string): Promise<ApiResponse<{
    api_key: string;
    prefix: string;
    created_at: string;
    warning: string;
  }>> {
    return this.client.post(`/integrations/${instanceId}/addon-key`);
  }

  async deleteIntegrationAddonKey(instanceId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/integrations/${instanceId}/addon-key`);
  }

  // --- Blocklist ---
  async getBlocklistRules(): Promise<ApiResponse<BlocklistRule[]>> {
    return this.client.get('/blocklist');
  }

  async createBlocklistRule(data: {
    field_name: string;
    match_value: string;
    match_type: string;
    scope: string;
    reason?: string;
  }, idempotencyKey?: string): Promise<ApiResponse<BlocklistRule>> {
    // DQ-354: the Idempotency-Key of the attempt.
    return this.client.post('/blocklist', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  async deleteBlocklistRule(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/blocklist/${id}`);
  }

  async getPersonalBlocklistRules(): Promise<ApiResponse<BlocklistRule[]>> {
    return this.client.get('/blocklist/personal');
  }

  async createPersonalBlocklistRule(data: {
    field_name: string;
    match_value: string;
    match_type: string;
    reason?: string;
  }): Promise<ApiResponse<BlocklistRule>> {
    return this.client.post('/blocklist/personal', data);
  }

  async deletePersonalBlocklistRule(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/blocklist/personal/${id}`);
  }

  // --- Email Templates ---
  async getEmailTemplates(params?: {
    template_key?: string;
    category?: string;
    locale?: string;
    organization_id?: string | null;
    include_inactive?: boolean;
  }): Promise<ApiResponse<EmailTemplate[]>> {
    return this.client.get('/email-templates', { params });
  }

  async getEmailTemplate(id: string): Promise<ApiResponse<EmailTemplate>> {
    return this.client.get(`/email-templates/${id}`);
  }

  async createEmailTemplate(data: {
    template_key: string;
    organization_id?: string | null;
    locale: string;
    name?: string | null;
    description?: string | null;
    category?: string | null;
    merge_schema_key?: string | null;
    subject_template: string;
    body_template: string;
    is_active?: boolean;
  }): Promise<ApiResponse<EmailTemplate>> {
    return this.client.post('/email-templates', data);
  }

  async updateEmailTemplate(id: string, data: Partial<{
    name: string | null;
    description: string | null;
    category: string | null;
    locale: string;
    merge_schema_key: string | null;
    subject_template: string;
    body_template: string;
    is_active: boolean;
  }>): Promise<ApiResponse<EmailTemplate>> {
    return this.client.patch(`/email-templates/${id}`, data);
  }

  async deleteEmailTemplate(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/email-templates/${id}`);
  }

  async previewEmailTemplate(id: string, variables?: Record<string, any>, unsaved?: { subject_template?: string; body_template?: string }): Promise<ApiResponse<EmailTemplatePreview>> {
    return this.client.post(`/email-templates/${id}/preview`, { variables, ...(unsaved || {}) });
  }

  async getEmailMergeSchemas(): Promise<ApiResponse<EmailTemplateMergeSchema[]>> {
    return this.client.get('/email-templates/merge-schemas');
  }

  async getEmailMergeSchema(key: string): Promise<ApiResponse<EmailTemplateMergeSchema>> {
    return this.client.get(`/email-templates/merge-schemas/${encodeURIComponent(key)}`);
  }

  async uploadEmailTemplateAsset(
    templateId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<EmailTemplateAsset>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post(`/email-templates/${templateId}/assets`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded * 100) / (e.total || 1))),
    });
  }

  // --- SLA / Reports ---
  async getSlaCompliance(filters?: ReportFilterValues): Promise<ApiResponse<any>> {
    return this.client.get('/reports/sla-compliance', { params: filters });
  }

  // --- Organization SLA update ---
  async updateOrganizationSla(id: string, data: {
    sla_response_hours?: number;
    sla_resolution_hours?: number;
    business_hours_start?: string;
    business_hours_end?: string;
    business_days?: number[];
  }): Promise<ApiResponse<any>> {
    return this.client.patch(`/organizations/${id}`, data);
  }

  // --- System Settings ---
  async getSystemSettings(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/system');
  }

  async updateSystemSettings(category: string, settings: Record<string, string>): Promise<ApiResponse<any>> {
    return this.client.patch('/settings/system', { category, settings });
  }

  /** D-278: the system VAT rate (percent), readable by every OTE employee. */
  async getVatRate(): Promise<ApiResponse<{ vat_rate: number }>> {
    return this.client.get('/settings/vat-rate');
  }

  async testSmtp(data: { host: string; port: number; secure: boolean; user: string; pass: string }): Promise<ApiResponse<any>> {
    return this.client.post('/settings/system/test-smtp', data);
  }

  async testAi(data: { provider: string; apiKey: string; model?: string }): Promise<ApiResponse<any>> {
    return this.client.post('/settings/system/test-ai', data);
  }

  async testTranscription(data: { provider: string; apiKey: string }): Promise<ApiResponse<any>> {
    return this.client.post('/settings/system/test-transcription', data);
  }

  async testGmail(data: { serviceAccountJson: string; impersonateEmail: string }): Promise<ApiResponse<any>> {
    return this.client.post('/settings/system/test-gmail', data);
  }

  // --- Deploy ---
  async getDeployHistory(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/deploy/history');
  }

  async getCurrentVersion(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/deploy/current-version');
  }

  // --- Attendance ---
  async clockIn(notes?: string, location_type?: string): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post('/attendance/clock-in', { notes, location_type });
  }

  /** P-37 (D-194 Q-185) — the days closed automatically at 00:00 (pending = the morning notice). */
  async getAutoCloseouts(scope: 'pending' | 'recent' = 'pending'): Promise<ApiResponse<AutoCloseout[]>> {
    return this.client.get('/attendance/auto-closeouts', { params: { scope } });
  }

  /** P-37 — [לאשר] (the suggested times) / [לשנות] (the given ones); again later too. */
  async resolveAutoCloseout(
    id: string,
    body: { action: 'confirm' | 'change'; clock_out_at?: string | null; timers?: Array<{ work_log_id: string; end_time: string }> }
  ): Promise<ApiResponse<AutoCloseout>> {
    return this.client.post(`/attendance/auto-closeouts/${id}/resolve`, body);
  }

  async clockOut(notes?: string): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post('/attendance/clock-out', { notes });
  }

  /** D-116 — "חזרה לעבודה": today's clock-out becomes a break; the day is open again. */
  async backToWork(): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post('/attendance/back-to-work', {});
  }

  /**
   * P-10e (D-85) — `stop`: ONLY the timers checked in the break window, each
   * with an optional "מה עשיתי". Empty = "להשאיר פעילים" (nothing stops).
   */
  async startBreak(stop: Array<{ timer_id: string; summary?: string }> = []): Promise<ApiResponse<AttendanceRecord & { paused_timers?: ResumableTimer[] }>> {
    return this.client.post('/attendance/break-start', { stop });
  }

  // B.21 — the response lists the timers paused by the break, offered for resume.
  async endBreak(): Promise<ApiResponse<AttendanceRecord & { resumable_timers?: ResumableTimer[] }>> {
    return this.client.post('/attendance/break-end');
  }

  async getAttendanceStatus(): Promise<ApiResponse<AttendanceStatus>> {
    return this.client.get('/attendance/status');
  }

  async getMyDailyAttendance(date: string): Promise<ApiResponse<{ events: AttendanceRecord[]; summary: AttendanceDailySummary }>> {
    return this.client.get(`/attendance/my/daily?date=${date}`);
  }

  async getMyMonthlyAttendance(month: string): Promise<ApiResponse<{ summaries: AttendanceDailySummary[]; events_by_date: Record<string, AttendanceRecord[]>; travel?: MonthTravel }>> {
    return this.client.get(`/attendance/my/monthly?month=${month}`);
  }

  // P-10d (D-79 §2) — the daily approval (confirm / dispute / manager confirm) was removed.

  async getUserDailyAttendance(userId: string, date: string): Promise<ApiResponse<{ events: AttendanceRecord[]; summary: AttendanceDailySummary }>> {
    return this.client.get(`/attendance/users/${userId}/daily?date=${date}`);
  }

  async getUserMonthlyAttendance(userId: string, month: string): Promise<ApiResponse<{ summaries: AttendanceDailySummary[]; events_by_date: Record<string, AttendanceRecord[]>; travel?: MonthTravel }>> {
    return this.client.get(`/attendance/users/${userId}/monthly?month=${month}`);
  }

  /** P-26 (QX-96): every active employee's attendance of one day, summed (OTE manager only). */
  async getDayAttendanceTotal(date: string): Promise<ApiResponse<{ date: string; present_minutes: number; employees: number; logged_minutes: number }>> {
    return this.client.get('/attendance/day-total', { params: { date } });
  }

  async adjustAttendance(userId: string, data: { event_type: string; recorded_at: string; reason: string }): Promise<ApiResponse<AttendanceRecord>> {
    return this.client.post(`/attendance/users/${userId}/adjust`, data);
  }

  /** D-296: one day's events + totals + whether its month is approved (locked), for the day-edit window. */
  async getAttendanceDay(userId: string, date: string): Promise<ApiResponse<{ user_id: string; date: string; events: AttendanceRecord[]; total_work_minutes: number; total_break_minutes: number; locked: boolean; travel_enabled?: boolean; accountant_report_sent?: boolean }>> {
    return this.client.get(`/attendance/day/${userId}/${date}`);
  }

  /** D-296: replaces the whole day (clock-in, clock-out, breaks) with one reason. */
  async saveAttendanceDay(userId: string, date: string, data: { clock_in: string | null; clock_out: string | null; breaks: { start: string; end: string }[]; reason: string; location_type?: 'office' | 'home' }): Promise<ApiResponse<{ locked: boolean }>> {
    return this.client.put(`/attendance/day/${userId}/${date}`, data);
  }

  /** D-305 #6: correct a day's work location (office/home). The employee until the month is approved; after that the OTE manager. */
  async setAttendanceDayLocation(userId: string, date: string, data: { location_type: 'office' | 'home'; reason: string }): Promise<ApiResponse<{ date: string; location_type: string; month_locked: boolean }>> {
    return this.client.put(`/attendance/day/${userId}/${date}/location`, data);
  }

  // D-295: `extra` = start_date / end_date / user_ids (the summary table's filter; the export follows the same one).
  async getAttendanceReport(month: string, orgId?: string, extra?: Record<string, string>): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({ month, ...(orgId ? { org_id: orgId } : {}), ...(extra ?? {}) });
    return this.client.get(`/attendance/report?${params.toString()}`);
  }

  async exportAttendanceCSV(month: string, orgId?: string, extra?: Record<string, string>): Promise<Blob> {
    const params = new URLSearchParams({ month, ...(orgId ? { org_id: orgId } : {}), ...(extra ?? {}) });
    const response = await this.client.get(`/attendance/export?${params.toString()}`, { responseType: 'blob' });
    return response as any;
  }

  async getAttendancePolicies(orgId?: string): Promise<ApiResponse<AttendancePolicy[]>> {
    const params = orgId ? `?org_id=${orgId}` : '';
    return this.client.get(`/attendance/policies${params}`);
  }

  async createAttendancePolicy(data: Partial<AttendancePolicy> & { organization_id: string }): Promise<ApiResponse<AttendancePolicy>> {
    return this.client.post('/attendance/policies', data);
  }

  async updateAttendancePolicy(id: string, data: Partial<AttendancePolicy>): Promise<ApiResponse<AttendancePolicy>> {
    return this.client.patch(`/attendance/policies/${id}`, data);
  }

  async deleteAttendancePolicy(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/attendance/policies/${id}`);
  }

  async getAttendanceVsTickets(month: string): Promise<ApiResponse<AttendanceVsTicketsRow[]>> {
    return this.client.get(`/reports/attendance-vs-tickets?month=${month}`);
  }

  /** "תיעוד הצוות" (documentation part 3): General Manager only, 403 otherwise. */
  async getTeamDocumentation(period: TeamDocPeriod): Promise<ApiResponse<TeamDocumentation>> {
    return this.client.get(`/reports/team-documentation?period=${period}`);
  }

  // --- Push Notifications ---
  async subscribePush(data: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<ApiResponse<void>> {
    return this.client.post('/push/subscribe', data);
  }

  async unsubscribePush(endpoint: string): Promise<ApiResponse<void>> {
    return this.client.delete('/push/unsubscribe', { data: { endpoint } });
  }

  async getVapidPublicKey(): Promise<ApiResponse<{ publicKey: string }>> {
    return this.client.get('/push/vapid-key');
  }

  /** D-322 (6): "send me a test notice" to my own devices. */
  // FIX-D323-F3: is Web Push ready on this server (booleans only; staff).
  async getPushStatus(): Promise<ApiResponse<{ ready: boolean; switched_off: boolean; missing: string[] }>> {
    return this.client.get('/push/status');
  }

  // FIX-D323-F4: create the VAPID pair on the server when missing (manager only; never returns a key).
  async enableBrowserPush(): Promise<ApiResponse<{ created: boolean; ready: boolean; switched_off: boolean }>> {
    return this.client.post('/push/enable');
  }

  async sendTestPush(): Promise<ApiResponse<{ configured: boolean; devices: number; sent: number; failed: number }>> {
    return this.client.post('/push/test');
  }

  // --- Dashboard Builder ---
  async getDashboards(): Promise<ApiResponse<Dashboard[]>> {
    return this.client.get('/dashboard-builder');
  }

  async getDashboard(id: string): Promise<ApiResponse<Dashboard>> {
    return this.client.get(`/dashboard-builder/${id}`);
  }

  async getDefaultDashboard(): Promise<ApiResponse<Dashboard>> {
    return this.client.get('/dashboard-builder/default/init');
  }

  async createDashboard(data: { name?: string; is_default?: boolean }): Promise<ApiResponse<Dashboard>> {
    return this.client.post('/dashboard-builder', data);
  }

  async updateDashboard(id: string, data: { name?: string; is_default?: boolean; config?: Record<string, any> }): Promise<ApiResponse<Dashboard>> {
    return this.client.patch(`/dashboard-builder/${id}`, data);
  }

  async deleteDashboard(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.client.delete(`/dashboard-builder/${id}`);
  }

  async duplicateDashboard(id: string): Promise<ApiResponse<Dashboard>> {
    return this.client.post(`/dashboard-builder/${id}/duplicate`);
  }

  async reorderDashboards(order: { id: string; sort_order: number }[]): Promise<ApiResponse<{ updated: boolean }>> {
    return this.client.patch('/dashboard-builder/reorder/batch', { order });
  }

  async saveDashboardWidgets(dashboardId: string, widgets: any[]): Promise<ApiResponse<DashboardWidget[]>> {
    return this.client.put(`/dashboard-builder/${dashboardId}/widgets`, { widgets });
  }

  async addWidget(dashboardId: string, widget: any): Promise<ApiResponse<DashboardWidget>> {
    return this.client.post(`/dashboard-builder/${dashboardId}/widgets`, widget);
  }

  async removeWidget(dashboardId: string, widgetId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.client.delete(`/dashboard-builder/${dashboardId}/widgets/${widgetId}`);
  }

  async getWidgetCatalog(): Promise<ApiResponse<WidgetCatalogItem[]>> {
    return this.client.get('/dashboard-builder/widget-catalog');
  }

  /** Persist one widget's configuration (declared filters only) and/or title. */
  async updateWidgetConfig(dashboardId: string, widgetId: string, data: { config?: Record<string, unknown>; title?: string | null }): Promise<ApiResponse<DashboardWidget>> {
    return this.client.patch(`/dashboard-builder/${dashboardId}/widgets/${widgetId}`, data);
  }

  // --- Dashboard templates (decision 107) ---
  async getDashboardTemplates(): Promise<ApiResponse<DashboardTemplate[]>> {
    return this.client.get('/dashboard-builder/templates');
  }

  async createDashboardTemplate(data: { name: string; description?: string; from_dashboard_id?: string; target_roles?: string[]; target_user_ids?: string[] }): Promise<ApiResponse<DashboardTemplate>> {
    return this.client.post('/dashboard-builder/templates', data);
  }

  async updateDashboardTemplate(id: string, data: { name?: string; description?: string | null; target_roles?: string[]; target_user_ids?: string[] }): Promise<ApiResponse<DashboardTemplate>> {
    return this.client.patch(`/dashboard-builder/templates/${id}`, data);
  }

  async setDashboardTemplatePublished(id: string, publish: boolean, reason?: string): Promise<ApiResponse<DashboardTemplate>> {
    return this.client.post(`/dashboard-builder/templates/${id}/${publish ? 'publish' : 'unpublish'}`, { reason: reason || null });
  }

  async deleteDashboardTemplate(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.client.delete(`/dashboard-builder/templates/${id}`);
  }

  async applyDashboardTemplate(id: string, name?: string): Promise<ApiResponse<Dashboard & { dropped_widgets: number }>> {
    return this.client.post(`/dashboard-builder/templates/${id}/apply`, name ? { name } : {});
  }

  // --- Contacts CRM ---
  async getContacts(params?: { organization_id?: string; search?: string; status?: 'active' | 'archived' | 'all'; limit?: number; offset?: number }): Promise<ApiResponse<Contact[]>> {
    return this.client.get('/contacts', { params });
  }

  async getContact(id: string): Promise<ApiResponse<Contact>> {
    return this.client.get(`/contacts/${id}`);
  }

  async createContact(data: Partial<Contact> & { organization_id: string; name: string }): Promise<ApiResponse<Contact>> {
    return this.client.post('/contacts', data);
  }

  /**
   * Create a contact, optionally together with its portal access.
   *
   * Omitting `password` creates a plain contact and nothing else. Supplying it
   * creates the customer-side login and its portal grant in the SAME request —
   * and `access_type` is then required by the server.
   */
  async createContactWithAccess(
    data: Partial<Contact> & {
      organization_id: string;
      name: string;
      password?: string;
      access_type?: ContactAccessType;
      /** P-18: the login e-mail when it differs from the contact's. */
      access_email?: string;
      approve_tickets?: boolean;
    }
  ): Promise<ApiResponse<{ contact: Contact; user: unknown | null; portal_access: unknown | null }> & { email_sent?: boolean }> {
    return this.client.post('/contacts/with-access', data);
  }

  /** D-147 (QX-79): portal access for an EXISTING contact — GM, or the AM of its customer. */
  async createContactPortalAccess(contactId: string, data: {
    access_type: ContactAccessType; password: string; email?: string; approve_tickets?: boolean;
  }): Promise<ApiResponse<PortalAccess> & { email_sent?: boolean }> {
    return this.client.post(`/contacts/${contactId}/portal-access`, data);
  }

  async updateContact(id: string, data: Partial<Contact>): Promise<ApiResponse<Contact>> {
    return this.client.patch(`/contacts/${id}`, data);
  }

  async deleteContact(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/contacts/${id}`);
  }

  async importContacts(file: File, organizationId: string): Promise<ApiResponse<Contact[]>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId);
    return this.client.post('/contacts/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getContactInteractions(contactId: string): Promise<ApiResponse<ContactInteraction[]>> {
    return this.client.get(`/contacts/${contactId}/interactions`);
  }

  async createContactInteraction(contactId: string, data: { interaction_type: string; summary?: string; ticket_id?: string }): Promise<ApiResponse<ContactInteraction>> {
    return this.client.post(`/contacts/${contactId}/interactions`, data);
  }

  // --- Dashboard Managed Clients ---
  async getManagedClients(): Promise<ApiResponse<any[]>> {
    return this.client.get('/dashboard/managed-clients');
  }

  // --- Daily Status ---
  async getDailyStatus(params?: {
    limit?: number;
    sort?: 'priority,due_date,stale';
    /** Only my own work, also for the General Manager (Home focus, agenda). */
    mine?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.client.get('/dashboard/daily-status', { params });
  }

  // --- Employee Workspace (FEATURE_EMPLOYEE_WORKSPACE) ---
  async getWaitingForMe(): Promise<ApiResponse<import('../types/workspace').WaitingForMeData>> {
    return this.client.get('/dashboard/waiting-for-me');
  }

  async getDeadlines(range: 'today' | 'week'): Promise<ApiResponse<import('../types/workspace').DeadlinesResponse>> {
    return this.client.get('/dashboard/deadlines', { params: { range } });
  }

  async getRecentConversations(limit?: number): Promise<ApiResponse<any[]>> {
    return this.client.get('/dashboard/recent-conversations', { params: { limit } });
  }

  async getConversations(params?: {
    search?: string;
    filter?: 'all' | 'unread' | 'awaiting_reply' | 'mine' | 'discussion' | 'tickets';
    /** P-45b (D-210) — conversation | discussion (none = both). */
    kind?: 'conversation' | 'discussion';
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<ConversationSummary[]>> {
    return this.client.get('/conversations', { params });
  }

  /** D-326 - the existing 1:1 chat with this person (open one first, else archived), or null. */
  async getDirectConversation(userId: string): Promise<ApiResponse<{ id: string; status: string; title: string } | null>> {
    return this.client.get('/conversations/direct', { params: { user_id: userId } });
  }

  /** D-326 - `chats` = the NUMBER OF CHATS with unread messages (menu counter, tab, floating bubble); `total` = messages. */
  async getChatUnreadCount(): Promise<ApiResponse<{ total: number; chats: number }>> {
    return this.client.get('/conversations/unread-count');
  }

  /** D-326 - heartbeat; answers who is online now (OTE side). */
  async chatPresence(): Promise<ApiResponse<{ online: string[] }>> {
    return this.client.post('/conversations/presence');
  }

  async chatTypingPing(ticketId: string): Promise<void> {
    await this.client.post(`/conversations/${ticketId}/typing`);
  }

  async getChatTyping(ticketId: string): Promise<ApiResponse<{ names: string[] }>> {
    return this.client.get(`/conversations/${ticketId}/typing`);
  }

  async getConversationsGrouped(params?: {
    search?: string;
    filter?: 'all' | 'unread' | 'awaiting_reply' | 'mine' | 'discussion' | 'tickets';
    /** P-45b (D-210) — conversation | discussion (none = both). */
    kind?: 'conversation' | 'discussion';
    status?: string;
  }): Promise<ApiResponse<ConversationGroup[]>> {
    return this.client.get('/conversations/grouped-by-customer', { params });
  }

  async ticketMentionSearch(params: {
    context_ticket_id: string;
    q?: string;
    limit?: number;
    scope?: 'all' | 'chat_group';
  }): Promise<ApiResponse<TicketMentionResult[]>> {
    return this.client.get('/tickets/mention-search', { params });
  }

  async forwardChatMessage(data: {
    source_ticket_id: string;
    comment_id: string;
    target_ticket_id: string;
  }): Promise<ApiResponse<any>> {
    return this.client.post('/conversations/forward', data);
  }

  // P-25a (D-161, D-163) — "היום שלי": the manager's cube and the role bands.
  async getNeedsAttention(): Promise<ApiResponse<import('../components/home/needsAttentionModel').NeedsAttentionData>> {
    return this.client.get('/dashboard/needs-attention');
  }
  async getRoleBands(): Promise<ApiResponse<import('../components/home/roleBandsModel').RoleBandsData>> {
    return this.client.get('/dashboard/role-bands');
  }
  async getDashboardAccess(): Promise<ApiResponse<{ has_dashboard: boolean; main_dashboard_id: string | null }>> {
    return this.client.get('/dashboard-builder/access');
  }
  async setMainDashboard(id: string): Promise<ApiResponse<{ main_dashboard_id: string | null }>> {
    return this.client.put(`/dashboard-builder/home/${id}`);
  }
  async clearMainDashboard(): Promise<ApiResponse<{ main_dashboard_id: string | null }>> {
    return this.client.delete('/dashboard-builder/home');
  }

  // --- Morning Brief ---
  async getMorningBrief(): Promise<ApiResponse<any>> {
    return this.client.get('/dashboard/morning-brief');
  }

  // --- Client Energy ---
  async getClientEnergy(filters?: ReportFilterValues): Promise<ApiResponse<any[]>> {
    return this.client.get('/reports/client-energy', { params: filters });
  }

  // --- Cash Flow ---
  async getCashFlowEntries(params?: { month?: string; type?: string }): Promise<ApiResponse<any[]>> {
    const q = toQuery(params);
    return this.client.get(`/cashflow${q ? '?' + q : ''}`);
  }

  async createCashFlowEntry(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/cashflow', data);
  }

  async updateCashFlowEntry(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/cashflow/${id}`, data);
  }

  async deleteCashFlowEntry(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/cashflow/${id}`);
  }

  /** P-50 (D-222): the cash-flow categories (income and expense). */
  async getCashFlowCategories(): Promise<ApiResponse<Array<{ id: string; name: string; type: 'income' | 'expense' }>>> {
    return this.client.get('/cashflow/categories');
  }

  async getCashFlowProjection(months?: number): Promise<ApiResponse<any>> {
    return this.client.get(`/cashflow/projection${months ? '?months=' + months : ''}`);
  }

  // --- Backups ---
  async getBackups(params?: { limit?: number; offset?: number; status?: string }): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups', { params });
  }

  async getBackupStats(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups/stats');
  }

  async getBackup(id: string): Promise<ApiResponse<any>> {
    return this.client.get(`/settings/backups/${id}`);
  }

  async deleteBackup(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/backups/${id}`);
  }

  async verifyBackup(id: string): Promise<ApiResponse<{ verified: boolean }>> {
    return this.client.post(`/settings/backups/${id}/verify`);
  }

  async downloadBackup(id: string): Promise<void> {
    const response = await this.client.get(`/settings/backups/${id}/download`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data as any]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup_${id}.tar.gz`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // --- Backup Schedules ---
  async getBackupSchedules(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups/schedules/list');
  }

  async createBackupSchedule(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/settings/backups/schedules', data);
  }

  async updateBackupSchedule(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/settings/backups/schedules/${id}`, data);
  }

  async deleteBackupSchedule(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/backups/schedules/${id}`);
  }

  // --- Backup Storage ---
  async getBackupStorageConfigs(): Promise<ApiResponse<any>> {
    return this.client.get('/settings/backups/storage/list');
  }

  async createBackupStorageConfig(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/settings/backups/storage', data);
  }

  async updateBackupStorageConfig(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/settings/backups/storage/${id}`, data);
  }

  async deleteBackupStorageConfig(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/settings/backups/storage/${id}`);
  }

  async testBackupStorageConfig(id: string): Promise<ApiResponse<{ connected: boolean }>> {
    return this.client.post(`/settings/backups/storage/${id}/test`);
  }

  // --- Leads ---
  async getLeads(params?: { status?: string; tab?: 'active' | 'converted' | 'irrelevant'; source?: string; assigned_to?: string; search?: string; created_from?: string; created_to?: string; activity_from?: string; activity_to?: string; limit?: number; offset?: number }): Promise<ApiResponse<Lead[]>> {
    return this.client.get('/leads', { params });
  }

  async getLead(id: string): Promise<ApiResponse<Lead>> {
    return this.client.get(`/leads/${id}`);
  }

  /** P-51 (D-224): the facts behind the stage bar and the "השלב הבא" box. */
  async getLeadJourney(id: string): Promise<ApiResponse<LeadJourneyFacts>> {
    return this.client.get(`/leads/${id}/journey`);
  }

  async getLeadTimeline(id: string): Promise<ApiResponse<unknown[]>> {
    return this.client.get(`/leads/${id}/timeline`);
  }

  async createLead(data: Partial<Lead>, idempotencyKey?: string): Promise<ApiResponse<Lead>> {
    // DQ-201: the Idempotency-Key of the attempt - a double click never creates two leads.
    return this.client.post('/leads', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  async updateLead(id: string, data: Partial<Lead> & { loss_reason_code?: string; loss_reason_text?: string }): Promise<ApiResponse<Lead>> {
    return this.client.patch(`/leads/${id}`, data);
  }

  /** P-46 (D-216 s.1): close a lead as "לא רלוונטי" - a reason from the list is required. */
  async closeLeadAsIrrelevant(id: string, reason: { code: string; text?: string }): Promise<ApiResponse<Lead>> {
    return this.client.patch(`/leads/${id}`, { lead_status: 'lost', loss_reason_code: reason.code, loss_reason_text: reason.text || undefined });
  }

  /** P-46: how much history a lead has - decides between "מחיקה" (empty only) and "סגירה כלא רלוונטי". */
  async getLeadHistory(id: string): Promise<ApiResponse<{ meetings: number; quotes: number; documentation: number; characterizations: number; empty: boolean }>> {
    return this.client.get(`/leads/${id}/history`);
  }

  async deleteLead(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/leads/${id}`);
  }

  /** Conversion preview: proposed customer/contact, duplicate candidates, commitment quotes. */
  async getLeadConversionPreview(id: string, quoteId?: string): Promise<ApiResponse<LeadConversionPreview>> {
    return this.client.get(`/leads/${id}/conversion-preview`, { params: quoteId ? { quote_id: quoteId } : undefined });
  }

  /** D-230 s.11 - is this login e-mail already taken? (checked when leaving the field) */
  async checkLeadConversionEmail(id: string, email: string): Promise<ApiResponse<ConversionEmailCheck>> {
    return this.client.get(`/leads/${id}/conversion-email-check`, { params: { email } });
  }

  /** Commit a lead conversion with the user's explicit link/create choices. */
  async convertLead(id: string, payload: LeadConvertPayload & { portal_access?: { email: string; password: string } }): Promise<ApiResponse<LeadConvertResult>> {
    return this.client.post(`/leads/${id}/convert`, payload);
  }

  async addLeadCommunication(leadId: string, data: { call_status: string; summary?: string; next_communication?: string; communication_date?: string }, idempotencyKey?: string): Promise<ApiResponse<LeadCommunication>> {
    // DQ-204: the Idempotency-Key of the attempt.
    return this.client.post(`/leads/${leadId}/communications`, data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  async getLeadCommunications(leadId: string): Promise<ApiResponse<LeadCommunication[]>> {
    return this.client.get(`/leads/${leadId}/communications`);
  }

  async addLeadProduct(leadId: string, data: { product_name: string; product_id?: string; quantity?: number; unit_price?: number; vat_rate?: number; notes?: string }): Promise<ApiResponse<LeadProduct>> {
    return this.client.post(`/leads/${leadId}/products`, data);
  }

  async removeLeadProduct(leadId: string, productId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/leads/${leadId}/products/${productId}`);
  }

  // --- Products ---
  async getProducts(params?: { active?: string; search?: string; category?: string; limit?: number; offset?: number }): Promise<ApiResponse<Product[]>> {
    return this.client.get('/products', { params });
  }

  async createProduct(data: Partial<Product>): Promise<ApiResponse<Product>> {
    return this.client.post('/products', data);
  }

  async updateProduct(id: string, data: Partial<Product>): Promise<ApiResponse<Product>> {
    return this.client.patch(`/products/${id}`, data);
  }

  async deleteProduct(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/products/${id}`);
  }

  // --- Quotes ---
  async getQuotes(params?: { organization_id?: string; lead_id?: string; status?: string; quote_type?: string; q?: string; limit?: number; offset?: number }): Promise<ApiResponse<Quote[]>> {
    return this.client.get('/quotes', { params });
  }

  async getQuote(id: string): Promise<ApiResponse<Quote>> {
    return this.client.get(`/quotes/${id}`);
  }

  async createQuote(data: Partial<Quote>, idempotencyKey?: string): Promise<ApiResponse<Quote>> {
    // DQ-211: the Idempotency-Key of the attempt - a double click never creates two quotes.
    return this.client.post('/quotes', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  async updateQuote(id: string, data: Partial<Quote>): Promise<ApiResponse<Quote>> {
    return this.client.patch(`/quotes/${id}`, data);
  }




  async duplicateQuote(id: string): Promise<ApiResponse<Quote>> {
    return this.client.post(`/quotes/${id}/duplicate`);
  }

  async addQuoteItem(quoteId: string, data: Partial<QuoteLineItem>): Promise<ApiResponse<QuoteLineItem>> {
    return this.client.post(`/quotes/${quoteId}/items`, data);
  }

  async updateQuoteItem(quoteId: string, itemId: string, data: Partial<QuoteLineItem>): Promise<ApiResponse<QuoteLineItem>> {
    return this.client.patch(`/quotes/${quoteId}/items/${itemId}`, data);
  }

  async deleteQuoteItem(quoteId: string, itemId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/quotes/${quoteId}/items/${itemId}`);
  }

  // --- Lead Pipeline Dashboard ---
  async getLeadPipeline(): Promise<ApiResponse<{ status: string; count: number; total_value: number }[]>> {
    return this.client.get('/dashboard/lead-pipeline');
  }

  async getProjectHealth(orgId?: string, filters?: ReportFilterValues): Promise<ApiResponse<any[]>> {
    return this.client.get('/reports/project-health', { params: { ...(orgId ? { org_id: orgId } : {}), ...(filters || {}) } });
  }

  async getAttendanceVsLogs(month?: string): Promise<ApiResponse<any[]>> {
    const params = month ? `?month=${month}` : '';
    return this.client.get(`/reports/attendance-vs-logs${params}`);
  }

  /** FIX-B11 (QX-26): the months I already approved (locked), YYYY-MM. Read-only. */
  async getMyLockedMonths(): Promise<ApiResponse<string[]>> {
    return this.client.get('/attendance/monthly/my/locked-months');
  }

  /** FQ-011: the open (unapproved) past months the server reminds about, YYYY-MM. */
  async getMyOpenMonths(): Promise<ApiResponse<string[]>> {
    return this.client.get('/attendance/monthly/my/open-months');
  }

  async getMyMonthlySummary(monthYear: string): Promise<ApiResponse<any>> {
    return this.client.get(`/attendance/monthly/my/${monthYear}`);
  }

  async getMonthlySummary(userId: string, monthYear: string): Promise<ApiResponse<any>> {
    return this.client.get(`/attendance/monthly/${userId}/${monthYear}`);
  }

  async generateMonthlySummary(userId: string, monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/${userId}/${monthYear}/generate`);
  }

  async confirmMyAttendanceMonthly(monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/my/${monthYear}/confirm`);
  }

  async confirmMyLogsMonthly(monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/my/${monthYear}/confirm-logs`);
  }

  async sendLogsForReviewMonthly(userId: string, monthYear: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/${userId}/${monthYear}/send-logs`);
  }

  /** D-319: the months I still have to approve (blocking window, 1st-of-month card, timer block). */
  async getMyMonthApprovalStatus(): Promise<ApiResponse<MonthApprovalStatus>> {
    return this.client.get('/attendance/monthly/my/approval-status');
  }

  /** D-319 s.5 step 2: all my logs of the month (+ the calendar names of its days). */
  async getMyMonthLogs(monthYear: string): Promise<ApiResponse<MonthLogsResponse>> {
    return this.client.get(`/attendance/monthly/my/${monthYear}/logs`);
  }

  /** D-319 s.5: the one approval - attendance + all logs (+ the service approval). */
  async confirmMyMonthAll(monthYear: string, confirmedLongLogIds: string[]): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/my/${monthYear}/confirm-all`, { confirmed_long_log_ids: confirmedLongLogIds });
  }

  /** D-319 s.3: the manager's month - status per employee, "X מתוך Y", the mail's state. */
  async getMonthApprovalOverview(monthYear: string): Promise<ApiResponse<MonthApprovalOverview>> {
    return this.client.get(`/attendance/monthly/approvals/${monthYear}`);
  }

  async remindMonthApproval(monthYear: string): Promise<ApiResponse<{ reminded: number }>> {
    return this.client.post(`/attendance/monthly/approvals/${monthYear}/remind`);
  }

  async sendMonthToAccountantsNow(monthYear: string): Promise<ApiResponse<{ outcome: string }>> {
    return this.client.post(`/attendance/monthly/approvals/${monthYear}/send-now`);
  }

  // FIX-ACC-PDF — corrected resend of the month's report to the accountants (OTE manager only).
  async resendCorrectedToAccountants(monthYear: string): Promise<ApiResponse<{ outcome: string }>> {
    return this.client.post(`/attendance/monthly/approvals/${monthYear}/resend-corrected`, {});
  }

  // P-10b (D-68) — מנהלת חשבונות (receives the monthly attendance report).
  async getAccountants(): Promise<ApiResponse<Accountant[]>> {
    return this.client.get('/accountants');
  }

  async createAccountant(data: { name: string; email: string; address?: string | null; note?: string | null }): Promise<ApiResponse<Accountant>> {
    return this.client.post('/accountants', data);
  }

  async updateAccountant(id: string, data: { name?: string; email?: string; address?: string | null; note?: string | null }): Promise<ApiResponse<Accountant>> {
    return this.client.patch(`/accountants/${id}`, data);
  }

  async deleteAccountant(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/accountants/${id}`);
  }

  // B.22 — OTE manager reopens an approved (locked) month, with a reason.
  async reopenAttendanceMonth(userId: string, monthYear: string, reason: string): Promise<ApiResponse<any>> {
    return this.client.post(`/attendance/monthly/${userId}/${monthYear}/reopen`, { reason });
  }

  // Meetings
  async getMeetings(params?: { from?: string; to?: string; status?: string }): Promise<ApiResponse<any[]>> {
    const q = toQuery(params);
    return this.client.get(`/meetings${q ? '?' + q : ''}`);
  }

  /** P-38b (D-192 §8) — the paged list: upcoming first (ascending), then the past (descending). */
  async getMeetingsPage(params: { status?: string; organization_id?: string; lead_id?: string; limit: number; offset: number }): Promise<ApiResponse<any[]>> {
    const q = toQuery(params);
    return this.client.get(`/meetings?${q}`);
  }

  /** P-38b — "מי המשתתפים?" for a customer or a lead (none: the staff only). */
  async getMeetingCandidates(params: { organization_id?: string; lead_id?: string }): Promise<ApiResponse<MeetingCandidates>> {
    const q = toQuery(params);
    return this.client.get(`/meetings/candidates${q ? '?' + q : ''}`);
  }

  /** P-38b — meetings of the chosen staff on one local day (D-192 §6). */
  async getMeetingConflicts(params: { date: string; user_ids: string[]; exclude_id?: string }): Promise<ApiResponse<MeetingConflicts>> {
    const q = toQuery({ date: params.date, user_ids: params.user_ids.join(','), exclude_id: params.exclude_id });
    return this.client.get(`/meetings/conflicts?${q}`);
  }

  /** P-38b — my own answer as an OTE participant. */
  async rsvpMeeting(id: string, status: 'accepted' | 'declined' | 'tentative'): Promise<ApiResponse<any>> {
    return this.client.post(`/meetings/${id}/rsvp`, { status });
  }

  /** P-38b — the portal: upcoming meetings the viewer takes part in. */
  async getPortalMeetings(): Promise<ApiResponse<PortalMeeting[]>> {
    return this.client.get('/portal/meetings');
  }

  async rsvpPortalMeeting(id: string, status: 'accepted' | 'declined'): Promise<ApiResponse<any>> {
    return this.client.post(`/portal/meetings/${id}/rsvp`, { status });
  }

  async getUpcomingMeetings(): Promise<ApiResponse<any[]>> {
    return this.client.get('/meetings/upcoming');
  }

  async getMeeting(id: string): Promise<ApiResponse<any>> {
    return this.client.get(`/meetings/${id}`);
  }

  async createMeeting(data: any): Promise<ApiResponse<any>> {
    return this.client.post('/meetings', data);
  }

  // FIX-D327 (D-327) - the employee's own Google Calendar connection.
  async getGcalStatus(): Promise<ApiResponse<{ configured: boolean; connected: boolean; google_email: string | null; needs_reconnect: boolean; last_sync_at: string | null; push: boolean }>> {
    return this.client.get('/gcal/status');
  }

  async getGcalConnectUrl(): Promise<ApiResponse<{ url: string }>> {
    return this.client.get('/gcal/connect-url');
  }

  async disconnectGcal(): Promise<ApiResponse<void>> {
    return this.client.post('/gcal/disconnect');
  }

  /** Events made in Google ("מיומן גוגל") of the signed-in employee, between two instants. */
  async getGcalEvents(from: string, to: string): Promise<ApiResponse<Array<{ id: string; summary: string | null; start_at: string; end_at: string; all_day: boolean; html_link: string | null }>>> {
    return this.client.get('/gcal/events', { params: { from, to } });
  }

  async updateMeeting(id: string, data: any): Promise<ApiResponse<any>> {
    return this.client.patch(`/meetings/${id}`, data);
  }

  async deleteMeeting(id: string): Promise<ApiResponse<any>> {
    return this.client.delete(`/meetings/${id}`);
  }

  async completeMeeting(id: string, data?: { follow_up_date?: string; follow_up_notes?: string; summary?: string }): Promise<ApiResponse<any>> {
    return this.client.patch(`/meetings/${id}/complete`, data || {});
  }

  async addMeetingParticipant(meetingId: string, data: { contact_id?: string; user_id?: string }): Promise<ApiResponse<any>> {
    return this.client.post(`/meetings/${meetingId}/participants`, data);
  }

  async removeMeetingParticipant(meetingId: string, participantId: string): Promise<ApiResponse<any>> {
    return this.client.delete(`/meetings/${meetingId}/participants/${participantId}`);
  }

  // Checklist
  async getChecklist(ticketId: string): Promise<ApiResponse<any[]>> {
    return this.client.get(`/tickets/${ticketId}/checklist`);
  }

  async addChecklistItem(ticketId: string, text: string): Promise<ApiResponse<any>> {
    return this.client.post(`/tickets/${ticketId}/checklist`, { text });
  }

  async updateChecklistItem(ticketId: string, itemId: string, data: { text?: string; completed?: boolean }): Promise<ApiResponse<any>> {
    return this.client.patch(`/tickets/${ticketId}/checklist/${itemId}`, data);
  }

  async deleteChecklistItem(ticketId: string, itemId: string): Promise<ApiResponse<any>> {
    return this.client.delete(`/tickets/${ticketId}/checklist/${itemId}`);
  }

  async reorderChecklist(ticketId: string, ids: string[]): Promise<ApiResponse<any>> {
    return this.client.post(`/tickets/${ticketId}/checklist/reorder`, { ids });
  }

  // Bug Reports
  async createBugReport(data: CreateBugReportPayload): Promise<ApiResponse<{ ticket: Ticket; context: BugReportContext }>> {
    return this.client.post('/bug-reports', data);
  }

  async getBugReport(ticketId: string): Promise<ApiResponse<{ ticket: Ticket; context: BugReportContext }>> {
    return this.client.get(`/bug-reports/${ticketId}`);
  }

  async getAgentReadableContext(ticketId: string): Promise<ApiResponse<AgentReadableContext>> {
    return this.client.get(`/bug-reports/${ticketId}/agent-context`);
  }

  async updateBugReport(ticketId: string, data: UpdateBugReportPayload): Promise<ApiResponse<BugReportContext>> {
    return this.client.patch(`/bug-reports/${ticketId}`, data);
  }

  async deleteBugReportContext(ticketId: string): Promise<ApiResponse<any>> {
    return this.client.delete(`/bug-reports/${ticketId}/context`);
  }

  async uploadBugReportScreenshot(
    ticketId: string,
    file: Blob,
    kind: 'region' | 'full' = 'region'
  ): Promise<ApiResponse<{ screenshot_path: string; screenshot_url: string }>> {
    const formData = new FormData();
    formData.append('screenshot', file, 'screenshot.png');
    return this.client.post(`/bug-reports/${ticketId}/screenshot`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: kind === 'full' ? { kind: 'full' } : undefined,
    });
  }

  // D-323: a voice note (dictation blocked / not wanted).
  async uploadBugReportAudio(ticketId: string, file: Blob): Promise<ApiResponse<{ audio_path: string }>> {
    const formData = new FormData();
    formData.append('audio', file, 'voice.webm');
    return this.client.post(`/bug-reports/${ticketId}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // D-341: one-time "what's new" messages, remembered per user on the server.
  async getAnnouncement(key: string): Promise<ApiResponse<{ key: string; seen: boolean }>> {
    return this.client.get(`/announcements/${key}`);
  }
  async markAnnouncementSeen(key: string): Promise<ApiResponse<{ key: string; seen: boolean }>> {
    return this.client.post(`/announcements/${key}/seen`);
  }

  // D-323: "הדיווחים שלי" - only the caller's own reports.
  async getMyBugReports(): Promise<ApiResponse<MyBugReport[]>> {
    return this.client.get('/bug-reports/mine');
  }

  // D-323: a stored image / voice note as a blob (the file route is authenticated, so an <img src> cannot fetch it).
  async getBugReportMedia(ticketId: string, kind: 'screenshot' | 'full' | 'audio'): Promise<Blob> {
    const res = await this.client.get(`/bug-reports/${ticketId}/media/${kind}`, { responseType: 'blob' });
    return res as unknown as Blob; // the response interceptor already unwraps `.data`
  }

  // D-323: "להעביר ל-Claude" / "אטפל בעצמי" (OTE manager).
  // D-341: "שליחה ל-Claude" may carry the manager's own note to Claude.
  async triageBugReport(ticketId: string, triage: BugReportTriage, note?: string): Promise<ApiResponse<BugReportContext>> {
    return this.client.patch(`/bug-reports/${ticketId}/triage`, { triage, ...(note ? { note } : {}) });
  }

  // D-341: the reporter writes back in the thread.
  async reporterMessageBugReport(ticketId: string, text: string): Promise<ApiResponse<{ status_history: MyBugReport['status_history'] }>> {
    return this.client.post(`/bug-reports/${ticketId}/reporter-message`, { text });
  }

  // D-323: the manager answers the reporter from the report.
  async replyBugReport(ticketId: string, text: string): Promise<ApiResponse<BugReportContext>> {
    return this.client.post(`/bug-reports/${ticketId}/reply`, { text });
  }

  // D-323: the treatment status / verdict (OTE manager).
  async reviewBugReport(ticketId: string, data: {
    report_status?: BugReportStatus; verdict?: BugReportVerdict; decision_ref?: string | null; note?: string;
  }): Promise<ApiResponse<BugReportContext>> {
    return this.client.patch(`/bug-reports/${ticketId}/status`, data);
  }

  async getBugReportStats(): Promise<ApiResponse<any>> {
    return this.client.get('/bug-reports/stats');
  }

  async uploadBugReportRecording(
    ticketId: string,
    data: { events: any[]; startedAt: string; duration_ms: number; viewport: { width: number; height: number } }
  ): Promise<ApiResponse<{ recording_path: string }>> {
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    const formData = new FormData();
    formData.append('recording', blob, 'recording.json');
    return this.client.post(`/bug-reports/${ticketId}/recording`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getBugReportList(params: {
    limit?: number;
    offset?: number;
    priority?: string;
    status?: string;
    report_status?: string;
    triage?: string;
    organization_id?: string;
    tag?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_dir?: string;
  }): Promise<ApiResponse<BugReportListItem[]>> {
    return this.client.get('/bug-reports/list', { params });
  }

  async bugReportBulkAction(data: {
    ticket_ids: string[];
    action: 'change_priority' | 'assign_agent';
    value: string;
  }): Promise<ApiResponse<{ affected: number }>> {
    return this.client.post('/bug-reports/bulk-action', data);
  }

  async getBugReportRecordingData(ticketId: string): Promise<ApiResponse<any>> {
    return this.client.get(`/bug-reports/${ticketId}/recording-data`);
  }

  async exportBugReportsCsv(params: {
    priority?: string;
    status?: string;
    organization_id?: string;
    tag?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<Blob> {
    const response = await this.client.get('/bug-reports/export-csv', { params, responseType: 'blob' });
    return response as any;
  }

  async getBugReportTrends(days?: number): Promise<ApiResponse<{
    daily: { day: string; count: number }[];
    top_tags: { tag: string; count: number }[];
    avg_resolution_hours: number | null;
    period_days: number;
  }>> {
    return this.client.get('/bug-reports/trends', { params: days ? { days } : undefined });
  }

  // --- Permissions ---
  async getPermissionRules(params?: {
    role?: string;
    permission?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<PermissionRule[]>> {
    return this.client.get('/permissions/rules', { params });
  }

  async createPermissionRule(data: {
    role: string;
    permission: PermissionName;
    scope?: string;
    entity_type?: string;
    field?: string;
    effect: 'allow' | 'deny';
  }): Promise<ApiResponse<PermissionRule>> {
    return this.client.post('/permissions/rules', data);
  }

  async deletePermissionRule(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/permissions/rules/${id}`);
  }

  async getPermissionCapabilities(): Promise<ApiResponse<Record<PermissionName, boolean>>> {
    return this.client.get('/permissions/capabilities');
  }

  // --- Screen permissions: groups, grants, the caller's effective set ---
  /** `{ isAdmin, screens, scopes }` for the signed-in user (404 on an older server). */
  async getMyPermissions(): Promise<unknown> {
    return this.client.get('/permissions/me');
  }

  async getPermissionCatalog(): Promise<ApiResponse<PermissionCatalogItem[]>> {
    return this.client.get('/permissions/catalog');
  }

  async getPermissionGroups(): Promise<ApiResponse<PermissionGroup[]>> {
    return this.client.get('/permissions/groups');
  }

  async createPermissionGroup(data: { name: string; description?: string }): Promise<ApiResponse<PermissionGroup>> {
    return this.client.post('/permissions/groups', data);
  }

  async updatePermissionGroup(id: string, data: { name?: string; description?: string }): Promise<ApiResponse<PermissionGroup>> {
    return this.client.patch(`/permissions/groups/${id}`, data);
  }

  async deletePermissionGroup(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/permissions/groups/${id}`);
  }

  async getPermissionGroupMembers(id: string): Promise<ApiResponse<PermissionGroupMember[]>> {
    return this.client.get(`/permissions/groups/${id}/members`);
  }

  async setPermissionGroupMembers(id: string, userIds: string[]): Promise<ApiResponse<PermissionGroupMember[]>> {
    return this.client.put(`/permissions/groups/${id}/members`, { userIds });
  }

  async getPermissionGrants(subjectType: PermissionSubjectType, subjectId: string): Promise<ApiResponse<PermissionGrant[]>> {
    return this.client.get('/permissions/grants', { params: { subjectType, subjectId } });
  }

  async setPermissionGrant(data: { subjectType: PermissionSubjectType; subjectId: string; key: string; effect: PermissionGrantEffect | 'inherit' }): Promise<ApiResponse<unknown>> {
    return this.client.put('/permissions/grants', data);
  }

  // D-335 — page access per employee ("לפי עובד"; the OTE manager only).
  async getScreenOverrideSummary(): Promise<ApiResponse<{ userId: string; count: number }[]>> {
    return this.client.get('/permissions/overrides/summary');
  }

  async getScreenOverrides(userId: string): Promise<ApiResponse<ScreenOverridesOfUser>> {
    return this.client.get('/permissions/overrides', { params: { userId } });
  }

  async setScreenOverride(data: { userId: string; key: string; access: ScreenOverrideLevel | 'inherit' }): Promise<ApiResponse<{ key: string; access: ScreenOverrideLevel | null }>> {
    return this.client.put('/permissions/overrides', data);
  }

  // FIX-PERMS-SCOPE — the data scope of one page for one employee ("כל העובדים" = read only, no money).
  async setScreenScope(data: { userId: string; key: string; scope: ScreenDataScope }): Promise<ApiResponse<{ key: string; scope: ScreenDataScope }>> {
    return this.client.put('/permissions/overrides/scope', data);
  }

  async resetScreenOverrides(userId: string): Promise<ApiResponse<{ removed: number }>> {
    return this.client.delete('/permissions/overrides', { params: { userId } });
  }

  // --- Portal Access ---
  async getPortalAccess(params?: {
    organization_id?: string;
    user_id?: string;
    contact_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<PortalAccess[]>> {
    return this.client.get('/portal-access', { params });
  }

  // D-242 - the employees tab + admin password actions
  async getPortalAccessEmployees(): Promise<ApiResponse<Array<{ employee_profile_id: string; user_id: string; employee_name: string; user_name: string | null; email: string | null; user_role: string; user_active: boolean; employee_active: boolean; grant_id: string | null; grant_active: boolean | null }>>> {
    return this.client.get('/portal-access/employees');
  }

  async sendEmployeePasswordLink(userId: string): Promise<ApiResponse<{ email_sent: boolean }> & { email_sent?: boolean }> {
    return this.client.post(`/portal-access/employees/${userId}/send-password-link`);
  }

  /** D-249: the one access save - login e-mail and/or password (admin). The server sends the mails. */
  async updateEmployeeCredentials(userId: string, data: { email?: string; password?: string; seniority?: 'regular' | 'senior' }): Promise<ApiResponse<{ email_changed: boolean; password_changed: boolean; email_sent: boolean }> & { email_sent?: boolean; warning?: string }> {
    return this.client.patch(`/portal-access/employees/${userId}/credentials`, data);
  }

  async setEmployeePassword(userId: string, password: string): Promise<ApiResponse<{ password_changed: boolean; email_sent?: boolean }> & { email_sent?: boolean; warning?: string }> {
    return this.client.post(`/portal-access/employees/${userId}/set-password`, { password });
  }

  async createPortalAccess(data: {
    mode?: 'employee' | 'contact' | 'new_customer_employee';
    user_id?: string;
    organization_id?: string;
    contact_id?: string;
    portal_role?: string;
    permissions?: Record<string, any>;
    active?: boolean;
    employee_profile_id?: string;
    seniority?: 'regular' | 'senior';
    access_type?: 'customer_manager' | 'primary_contact' | 'customer_employee' | 'unclassified';
    name?: string;
    email?: string;
    password?: string;
    approve_tickets?: boolean;
  }): Promise<ApiResponse<PortalAccess> & { email_sent?: boolean }> {
    return this.client.post('/portal-access', data);
  }

  /** W-07: a plain customer employee WITHOUT a contact (login + grant only). */
  async createCustomerStaffAccess(data: {
    organization_id: string; name: string; email: string; password: string; active?: boolean;
  }): Promise<ApiResponse<PortalAccess> & { email_sent?: boolean }> {
    return this.client.post('/portal-access/customer-staff', data);
  }

  async updatePortalAccess(id: string, data: Partial<{
    contact_id: string;
    portal_role: string;
    permissions: Record<string, any>;
    active: boolean;
    organization_id: string;
    password: string;
    seniority: 'regular' | 'senior';
    access_type: 'customer_manager' | 'primary_contact' | 'customer_employee' | 'unclassified';
    approve_tickets: boolean;
  }>): Promise<ApiResponse<PortalAccess> & { email_sent?: boolean }> {
    return this.client.patch(`/portal-access/${id}`, data);
  }

  async deletePortalAccess(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/portal-access/${id}`);
  }

  // P-21 (D-152): customer-access actions — the GM, or the Account Manager on their customers.
  async getPortalAccessCeiling(): Promise<ApiResponse<{ is_general_manager: boolean; ceiling: Record<'view_projects' | 'view_documents' | 'view_hours' | 'view_billing', boolean> }>> {
    return this.client.get('/portal-access/ceiling');
  }

  async updateCustomerAccess(id: string, data: Partial<{
    access_type: 'customer_manager' | 'primary_contact' | 'customer_employee';
    permissions: Record<string, boolean>;
    active: boolean;
  }>): Promise<ApiResponse<PortalAccess>> {
    return this.client.patch(`/portal-access/customer/${id}`, data);
  }

  /** D-158 (QX-88): revoke a customer grant — the GM, or the Account Manager on their customers. */
  async revokeCustomerAccess(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/portal-access/customer/${id}`);
  }

  /** P-27 (D-166 s.11): stop a queued credentials e-mail (409 once it was sent). */
  async cancelScheduledEmail(id: string): Promise<ApiResponse<{ id: string; status: 'cancelled' }>> {
    return this.client.post(`/scheduled-emails/${id}/cancel`, {});
  }

  /** "שליחה שוב": queue a new credentials e-mail from a cancelled one. */
  async resendScheduledEmail(id: string): Promise<ApiResponse<{ id: string; send_at: string; delay_ms: number; recipient_email: string }>> {
    return this.client.post(`/scheduled-emails/${id}/resend`, {});
  }

  async resendCustomerWelcome(id: string): Promise<ApiResponse<{ password_reset: boolean }> & { email_sent?: boolean }> {
    return this.client.post(`/portal-access/customer/${id}/resend-welcome`, {});
  }

  async resetCustomerPassword(id: string): Promise<ApiResponse<{ password_reset: boolean }> & { email_sent?: boolean }> {
    return this.client.post(`/portal-access/customer/${id}/reset-password`, {});
  }

  async linkCustomerAccessContact(id: string, contactId: string): Promise<ApiResponse<PortalAccess>> {
    return this.client.post(`/portal-access/customer/${id}/link-contact`, { contact_id: contactId });
  }

  async linkCustomerAccessEmployee(id: string, employeeProfileId: string): Promise<ApiResponse<PortalAccess>> {
    return this.client.post(`/portal-access/customer/${id}/link-employee`, { employee_profile_id: employeeProfileId });
  }

  // --- Customer Portal (customer-facing surface, §50/§51) ---
  async getPortalContext(): Promise<ApiResponse<{ organizations: PortalOrg[] }>> {
    return this.client.get('/portal/me');
  }

  async getPortalPermissions(): Promise<ApiResponse<PortalPermissionsResponse>> {
    return this.client.get('/portal/permissions');
  }

  async getPortalSummary(params?: { organization_id?: string }): Promise<ApiResponse<PortalSummaryData>> {
    return this.client.get('/portal/summary', { params });
  }

  async getPortalEngagements(params?: { organization_id?: string; type?: string }): Promise<ApiResponse<PortalEngagement[]>> {
    return this.client.get('/portal/engagements', { params });
  }

  async getPortalCharacterizations(params?: { organization_id?: string }): Promise<ApiResponse<PortalCharacterization[]>> {
    return this.client.get('/portal/characterizations', { params });
  }

  async getPortalMilestones(params?: { organization_id?: string; engagement_id?: string }): Promise<ApiResponse<PortalMilestone[]>> {
    return this.client.get('/portal/milestones', { params });
  }

  /** D-273 - the hour-bank breakdowns OTE sent (money viewers: manager / primary contact). */
  async getPortalHourBankReports(params?: { organization_id?: string; engagement_id?: string }): Promise<ApiResponse<Array<{
    id: string; engagement_id: string; engagement_name: string | null; organization_id: string; sent_at: string; period_start: string; period_end: string;
  }>>> {
    return this.client.get('/portal/hour-bank-reports', { params });
  }

  downloadPortalHourBankReport(id: string, fallbackName: string): Promise<void> {
    return this.downloadFile(`/portal/hour-bank-reports/${id}/pdf`, fallbackName);
  }

  async getPortalDocuments(params?: { organization_id?: string }): Promise<ApiResponse<PortalDocument[]>> {
    return this.client.get('/portal/documents', { params });
  }

  async getPortalHours(params?: { organization_id?: string; from?: string; to?: string }): Promise<ApiResponse<PortalHoursRow[]>> {
    return this.client.get('/portal/hours', { params });
  }

  async getPortalObligations(params?: { organization_id?: string }): Promise<ApiResponse<PortalObligation[]>> {
    return this.client.get('/portal/obligations', { params });
  }

  // --- Portal workspace (decisions 86/87) ---
  async getPortalDashboard(params?: { organization_id?: string }): Promise<ApiResponse<PortalDashboardData>> {
    return this.client.get('/portal/dashboard', { params });
  }

  async getPortalRecentNotifications(params?: { organization_id?: string; limit?: number }): Promise<ApiResponse<PortalRecentNotification[]>> {
    return this.client.get('/portal/recent-notifications', { params });
  }

  async getPortalPendingActions(params?: { organization_id?: string }): Promise<ApiResponse<PendingActionsResponse>> {
    return this.client.get('/portal/pending-actions', { params });
  }

  async getPortalRequests(params?: { organization_id?: string; status?: 'open' | 'closed'; kind?: 'ticket'; archived?: boolean; limit?: number; offset?: number }): Promise<ApiResponse<PortalRequest[]> & { held?: PortalTicketApproval[]; meta?: { total: number; limit: number; offset: number } }> {
    return this.client.get('/portal/requests', { params });
  }

  /** P-45c (D-210): conversations and discussions the caller takes part in (read and reply only). */
  async getPortalConversations(params?: { organization_id?: string; status?: 'open' | 'closed'; limit?: number; offset?: number }): Promise<ApiResponse<PortalConversation[]> & { meta?: { total: number; limit: number; offset: number; unread_total: number } }> {
    return this.client.get('/portal/conversations', { params });
  }

  async getPortalConversation(id: string, params?: { organization_id?: string }): Promise<ApiResponse<PortalConversation>> {
    return this.client.get(`/portal/conversations/${id}`, { params });
  }

  /** `pending_approval`: held for the customer's ticket approver (data = the approval row), else data = the ticket. */
  async createPortalRequest(data: CreatePortalRequestInput): Promise<ApiResponse<PortalRequest & PortalTicketApproval & { pending_approval: boolean }>> {
    return this.client.post('/portal/requests', data);
  }

  // --- Customer ticket approver (approver spec §3.4) ---
  async getPortalTicketApprovals(params?: { organization_id?: string; status?: TicketApprovalStatus }): Promise<ApiResponse<PortalTicketApproval[]>> {
    return this.client.get('/portal/ticket-approvals', { params });
  }

  async getPortalTicketApprovalsCount(params?: { organization_id?: string }): Promise<ApiResponse<{ pending: number }>> {
    return this.client.get('/portal/ticket-approvals/count', { params });
  }

  async approvePortalTicket(id: string): Promise<ApiResponse<PortalTicketApproval>> {
    return this.client.post(`/portal/ticket-approvals/${id}/approve`);
  }

  async rejectPortalTicket(id: string, reason: string): Promise<ApiResponse<PortalTicketApproval>> {
    return this.client.post(`/portal/ticket-approvals/${id}/reject`, { reason });
  }

  /** B3 (NL-07): one request the caller may see (404 when missing or not theirs). */
  async getPortalRequest(id: string, params?: { organization_id?: string }): Promise<ApiResponse<PortalRequest>> {
    return this.client.get(`/portal/requests/${id}`, { params });
  }

  /** B3 (NL-07): the request's public conversation, read only, oldest first. */
  async getPortalRequestMessages(id: string, params?: { organization_id?: string }): Promise<ApiResponse<PortalRequestMessage[]>> {
    return this.client.get(`/portal/requests/${id}/messages`, { params });
  }

  /** D-255: who is working on the request right now - display names only. */
  async getPortalRequestWorkingNow(id: string, params?: { organization_id?: string }): Promise<ApiResponse<Array<{ name: string }>>> {
    return this.client.get(`/portal/requests/${id}/working-now`, { params });
  }

  // --- Employee Profiles ---
  async getEmployeeProfiles(params?: {
    organization_id?: string;
    user_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<EmployeeProfile[]>> {
    return this.client.get('/employee-profiles', { params });
  }

  async getEmployeeProfileByUser(userId: string): Promise<ApiResponse<EmployeeProfile>> {
    return this.client.get(`/employee-profiles/user/${userId}`);
  }

  /**
   * Create a LOGIN-LESS OTE Employee (Stage יז, t5).
   *
   * This is the ONLY creation shape this screen offers: an employee is a
   * standalone business identity, so `name` is required (the employee owns its
   * business identity; there is no login to derive one from). Attaching an
   * employee to an EXISTING login ("חבר למשתמש קיים") was removed — it is not a
   * creation path (spec §13.7). Creating an employee TOGETHER with a NEW login
   * is a different, atomic operation — `createEmployeeProfileWithAccess`.
   */
  async createEmployeeProfile(data: {
    name: string;
    email?: string;
    employment_type?: string;
    hourly_cost?: number;
    monthly_cost?: number;
    work_hours_per_day?: number;
    work_days?: number[];
    employment_info?: Record<string, unknown>;
  }): Promise<ApiResponse<EmployeeProfile>> {
    return this.client.post('/employee-profiles', data);
  }

  async updateEmployeeProfile(userId: string, data: Partial<{
    employment_type: string;
    hourly_cost: number;
    monthly_cost: number;
    work_hours_per_day: number;
    work_days: number[];
    employment_info: Record<string, unknown>;
    /** P-13 (D-108) — YYYY-MM-DD. */
    employment_start_date: string;
    /** P-13 (D-108) — the date a cost change applies from (default today). */
    cost_effective_from: string;
    /** The EMPLOYEE's own business name (t5), mirrored to the login if linked. */
    name: string;
    /** Only meaningful when the employee HAS a login (spec §7.2.3). */
    password: string;
    /** Required with `password` when editing YOUR OWN record (the server
     *  verifies it); not needed when the General Manager sets another
     *  employee's password. */
    current_password: string;
    /** P-20 (D-148): goes through the one activation / deactivation service. */
    active: boolean;
    /** D-148 — free text "תפקיד". */
    job_title: string | null;
    /** D-148 bug fix — saved, and the login e-mail moves with it (409 when taken). */
    email: string;
    phone: string;
    address: string;
    notes: string;
    /** D-305 — OTE manager only (the server refuses an employee). '' / null clears. */
    travel_per_day: number | null;
    travel_monthly_cap: number | null;
    /** D-306 — 9 digits + check digit (400 otherwise). The employee may edit their own. */
    id_number: string | null;
    phone_secondary: string | null;
  }>): Promise<ApiResponse<EmployeeProfile> & { email_sent?: boolean; warning?: string }> {
    return this.client.patch(`/employee-profiles/${userId}`, data);
  }

  /**
   * Create an OTE Employee AND their login in ONE call (Stage יז, t2).
   *
   * Deliberately not two requests: if the profile call succeeded and the
   * identity call failed, the result would be an orphaned `employee_profiles`
   * row (or, in the reverse order, an orphaned login) — the exact divergence
   * the dual-identity model forbids. The server performs both writes in one
   * transaction; `role` is decided server-side and is NOT a parameter here.
   */
  async createEmployeeProfileWithAccess(data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    phone_extension?: string;
    employment_type?: string;
    hourly_cost?: number;
    monthly_cost?: number;
    work_hours_per_day?: number;
    work_days?: number[];
    employment_info?: Record<string, unknown>;
  }): Promise<ApiResponse<{ user: any; profile: EmployeeProfile }>> {
    return this.client.post('/employee-profiles/with-access', data);
  }

  async deleteEmployeeProfile(userId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/employee-profiles/${userId}`);
  }

  /** P-13 (D-108) — the employee's cost versions, newest first (OTE manager only). */
  async getEmployeeCostHistory(id: string): Promise<ApiResponse<EmployeeCostVersion[]>> {
    return this.client.get(`/employee-profiles/${id}/cost-history`);
  }

  // --- P-20 (D-148, D-151): the employee page ---
  async getEmployeeSystemRoles(id: string): Promise<ApiResponse<EmployeeSystemRoles>> {
    return this.client.get(`/employee-profiles/${id}/system-roles`);
  }

  async setEmployeeOteManager(id: string, enabled: boolean): Promise<ApiResponse<EmployeeSystemRoles>> {
    return this.client.put(`/employee-profiles/${id}/system-roles/ote-manager`, { enabled });
  }

  async addEmployeeRoleLink(id: string, kind: EmployeeRoleKind, entityId: string): Promise<ApiResponse<EmployeeSystemRoles>> {
    return this.client.post(`/employee-profiles/${id}/system-roles/${kind}`, { entity_id: entityId });
  }

  async removeEmployeeRoleLink(id: string, kind: EmployeeRoleKind, entityId: string, replacementUserId?: string | null): Promise<ApiResponse<EmployeeSystemRoles>> {
    return this.client.delete(`/employee-profiles/${id}/system-roles/${kind}/${entityId}`, {
      params: replacementUserId ? { replacement_user_id: replacementUserId } : undefined,
    });
  }

  async getEmployeeDeactivationPreview(id: string): Promise<ApiResponse<EmployeeDeactivationPreview>> {
    return this.client.get(`/employee-profiles/${id}/deactivation-preview`);
  }

  async deactivateEmployee(id: string, body: EmployeeDeactivateBody): Promise<ApiResponse<{ transferred_tasks: number; role_changes: number; stopped_timers: Array<{ ticket_title: string | null; error?: string }> }>> {
    return this.client.post(`/employee-profiles/${id}/deactivate`, body);
  }

  async activateEmployee(id: string, sendPasswordEmail: boolean): Promise<ApiResponse<{ login_restored: boolean; email_sent?: boolean }> & { email_sent?: boolean }> {
    return this.client.post(`/employee-profiles/${id}/activate`, { send_password_email: sendPasswordEmail });
  }

  async uploadEmployeePhoto(id: string, file: File): Promise<ApiResponse<{ photo_updated_at: string }>> {
    const formData = new FormData();
    formData.append('photo', file);
    return this.client.post(`/employee-profiles/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  async deleteEmployeePhoto(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/employee-profiles/${id}/photo`);
  }

  /** What an employee delete takes with it — drives the confirmation warning. */
  async getEmployeeDeleteImpact(userId: string): Promise<ApiResponse<EmployeeDeleteImpact>> {
    return this.client.get(`/employee-profiles/${userId}/delete-impact`);
  }

  // --- Engagements ---
  /** D-272 - the customer's active hour bank by the server's own rule (the 409 guard's), or null. */
  async getActiveHourBank(organizationId: string): Promise<ApiResponse<{ engagement_id: string; bank_id: string; name: string } | null>> {
    return this.client.get('/engagements/active-hour-bank', { params: { organization_id: organizationId } });
  }

  async getEngagements(params?: {
    organization_id?: string;
    type?: EngagementType;
    status?: EngagementStatus;
    include_deleted?: boolean;
    /** SEC-2b (D-106) — every OPEN engagement, for an employee's self-task. */
    for_self_task?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Engagement[]>> {
    return this.client.get('/engagements', { params });
  }

  /**
   * SEC-2b (D-106) — the customers with an open engagement (the self-task picker).
   * P-33a (D-188): the first row is "OTE, עבודה פנימית" (is_internal: true).
   */
  async getSelfTaskCustomers(): Promise<ApiResponse<Array<{
    id: string; name: string; is_internal?: boolean; internal_work_engagement_id?: string; internal_work_project_id?: string | null;
  }>>> {
    return this.client.get('/engagements/self-task-customers');
  }

  /** P-33a (D-185 / D-188) — the company's "עבודה פנימית" (null before the bootstrap). */
  async getInternalWork(): Promise<ApiResponse<InternalWork | null>> {
    return this.client.get('/engagements/internal-work');
  }

  /** D-274 §3 — the open projects of "עבודה פנימית OTE", with their milestones (every OTE employee). */
  async getInternalProjects(): Promise<ApiResponse<InternalProjectOption[]>> {
    return this.client.get('/engagements/internal-projects');
  }

  /** SEC-2b (D-94 / D-106) — may the caller open a task for anyone here, or only for themself? */
  async getTaskCreatePermissions(params?: { organization_id?: string; project_id?: string }): Promise<ApiResponse<{
    can_manage: boolean; manages_any: boolean; self_user_id: string;
  }>> {
    return this.client.get('/tickets/create-permissions', { params });
  }

  /** EQ-018 - the change history (audit rows) of a service contract / hour bank / LLH; money viewers only. */
  async getEngagementHistory(id: string): Promise<ApiResponse<unknown[]>> {
    return this.client.get(`/engagements/${id}/history`);
  }

  async getEngagement(id: string): Promise<ApiResponse<Engagement & {
    organization_name: string;
    child: (EngagementChild & Record<string, any>) | null;
    /** P-03/C3 — the financial fields still missing (service contract / hour bank / לל"ח). */
    financials?: { complete: boolean; missing_fields: string[] };
    /** P-07 (D-74 §8) — the "אפיון" card this engagement was opened from. */
    characterization?: CharacterizationBanner | null;
    /** SEC-2b (D-95) — edit: GM / Account Manager; the sums: + the PM of this project. */
    permissions?: { can_edit: boolean; can_see_money: boolean; can_see_log_control?: boolean };
  }>> {
    return this.client.get(`/engagements/${id}`);
  }

  /**
   * P-03/B — a service contract / hour bank / לל"ח is created WITH its
   * commercial terms (`TypedEngagementTerms`; required per type, 400 otherwise).
   */
  async createEngagement(data: {
    type: EngagementType;
    organization_id?: string;
    name: string;
    status?: EngagementStatus;
    /** P-07 (D-74 §8) — opened from an "אפיון" card: linked in the same transaction. */
    characterization_id?: string;
  } & TypedEngagementTerms): Promise<ApiResponse<Engagement>> {
    return this.client.post('/engagements', data);
  }

  /** P-03/D6 — edit name, dates and commercial terms (audited). */
  async updateEngagement(id: string, data: { name?: string } & TypedEngagementTerms): Promise<ApiResponse<{ changed: string[] }>> {
    return this.client.patch(`/engagements/${id}`, data);
  }

  /** P-02c/F-03 — cancelling a project / development needs `cancellationReason` (400 without it). */
  /** D-303: ending a project / development (`completed`) needs `warranty` (a date, or no_warranty) - 400 without it. */
  async updateEngagementStatus(id: string, status: EngagementStatus, cancellationReason?: string, warranty?: { warranty_until: string | null; no_warranty: boolean }): Promise<ApiResponse<Engagement & { child_status: string }>> {
    return announceBillingWarnings(
      await this.client.patch(`/engagements/${id}/status`, {
        status,
        ...(cancellationReason ? { cancellation_reason: cancellationReason } : {}),
        ...(warranty ?? {}),
      }),
      'cancel'
    );
  }

  async archiveEngagement(id: string): Promise<ApiResponse<{ archived: boolean }>> {
    return this.client.post(`/engagements/${id}/archive`);
  }

  async restoreEngagement(id: string): Promise<ApiResponse<{ restored: boolean }>> {
    return this.client.post(`/engagements/${id}/restore`);
  }

  /** D-329 - may the caller complete the engagement's financial data (OTE manager / its Account Manager); no sums. */
  async getEngagementFinancialsAccess(id: string): Promise<ApiResponse<{ can_complete: boolean; request_sent: boolean; organization_name: string | null }>> {
    return this.client.get(`/engagements/${id}/financials-access`);
  }

  /** D-329 - "שליחת בקשה להשלמה": a notice to the OTE manager and the customer's Account Manager. */
  async requestEngagementFinancials(id: string): Promise<ApiResponse<{ sent: boolean; already_sent: boolean }>> {
    return this.client.post(`/engagements/${id}/financials-request`);
  }

  /** D-331 Q3 - a customer opened without money fields: is it marked "חסרים נתונים כספיים" for the caller; no sums. */
  async getOrganizationFinancialsAccess(id: string): Promise<ApiResponse<{ missing: boolean; can_complete: boolean; request_sent: boolean; organization_name: string | null }>> {
    return this.client.get(`/organizations/${id}/financials-access`);
  }

  /** D-331 Q3 - "שליחת בקשה להשלמה" on a customer card. */
  async requestOrganizationFinancials(id: string): Promise<ApiResponse<{ sent: boolean; already_sent: boolean }>> {
    return this.client.post(`/organizations/${id}/financials-request`);
  }

  // D-265 sec.2-3 - the archive window: what goes with it, and the permanent delete (OTE manager).
  async getEngagementArchivePreview(id: string): Promise<ApiResponse<EngagementArchivePreview>> {
    return this.client.get(`/engagements/${id}/archive-preview`);
  }

  async purgeEngagement(id: string, confirmName: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.client.post(`/engagements/${id}/purge`, { confirm_name: confirmName });
  }

  async deleteEngagement(id: string, hard?: boolean): Promise<ApiResponse<{ archived?: boolean; deleted?: boolean; hard?: boolean }>> {
    return this.client.delete(`/engagements/${id}`, { params: hard ? { hard: true } : undefined });
  }

  // --- Stage ח: Service Contract (§25) ---
  async getServiceContract(engagementId: string): Promise<ApiResponse<ServiceContract & { engagement_id: string }>> {
    return this.client.get(`/engagements/${engagementId}/service-contract`);
  }

  async updateServiceContract(engagementId: string, data: Partial<ServiceContract>): Promise<ApiResponse<ServiceContract & { engagement_id: string }>> {
    return this.client.patch(`/engagements/${engagementId}/service-contract`, data);
  }

  // §26 / decision 30 — approve additional hours without the client: an
  // audited internal override (reason required).
  async approveServiceExtraHours(engagementId: string, data: { hours: number; expires_at?: string | null; reason: string; price?: number | null }): Promise<ApiResponse<ExtraHourRequest>> {
    return this.client.post(`/engagements/${engagementId}/service-contract/overtime-approval`, data);
  }

  // Decisions 29/30 — additional-hours requests (client approves in the portal).
  async listExtraHourRequests(engagementId: string): Promise<ApiResponse<ExtraHourRequest[]>> {
    return this.client.get(`/engagements/${engagementId}/service-contract/extra-hours-requests`);
  }

  async requestExtraHours(engagementId: string, data: { hours: number; price?: number | null; month?: string | null; note?: string | null }): Promise<ApiResponse<ExtraHourRequest>> {
    return this.client.post(`/engagements/${engagementId}/service-contract/extra-hours-requests`, data);
  }

  async overrideExtraHourRequest(engagementId: string, requestId: string, reason: string): Promise<ApiResponse<ExtraHourRequest>> {
    return this.client.post(`/engagements/${engagementId}/service-contract/extra-hours-requests/${requestId}/override`, { reason });
  }

  async cancelExtraHourRequest(engagementId: string, requestId: string): Promise<ApiResponse<ExtraHourRequest>> {
    return this.client.post(`/engagements/${engagementId}/service-contract/extra-hours-requests/${requestId}/cancel`);
  }

  async listPortalExtraHourRequests(): Promise<ApiResponse<Array<ExtraHourRequest & { contract_name: string; organization_id?: string }>>> {
    return this.client.get('/portal/extra-hour-requests');
  }

  async decidePortalExtraHourRequest(requestId: string, data: { decision: 'approved' | 'rejected'; reason?: string | null }): Promise<ApiResponse<ExtraHourRequest>> {
    return this.client.post(`/portal/extra-hour-requests/${requestId}/decision`, data);
  }

  // D-262 s.1 (QX-1241): the לל"ח hours that wait for the customer. Portal approver: list + decide (ids, or all).
  async listPortalHoursApprovals(): Promise<ApiResponse<PendingLllHour[]>> {
    return this.client.get('/portal/hours-approvals');
  }

  async decidePortalHoursApprovals(data: { decision: 'approved' | 'rejected'; ids?: string[]; all?: boolean; reason?: string | null }): Promise<ApiResponse<{ decided: number; skipped: number }>> {
    return this.client.post('/portal/hours-approvals/decision', data);
  }

  // OTE side: the pending hours of one לל"ח, and "אושר ע״י הלקוח" in his name (a reason is required).
  async listLllPendingHours(engagementId: string): Promise<ApiResponse<PendingLllHour[]>> {
    return this.client.get(`/engagements/${engagementId}/service-without-contract/pending-hours`);
  }

  async approveLllHoursOnBehalf(engagementId: string, data: { ids: string[]; reason: string }): Promise<ApiResponse<{ decided: number; skipped: number }>> {
    return this.client.post(`/engagements/${engagementId}/service-without-contract/hours-approval`, data);
  }

  // Decision 36 — effective-dated engagement prices.
  async listEngagementRates(engagementId: string): Promise<ApiResponse<EngagementRate[]>> {
    return this.client.get(`/engagements/${engagementId}/rates`);
  }

  async createEngagementRate(engagementId: string, data: { rate: number; rate_type?: 'hourly' | 'overtime'; effective_from?: string | null; reason?: string | null }): Promise<ApiResponse<EngagementRate>> {
    return this.client.post(`/engagements/${engagementId}/rates`, data);
  }

  // BQ-069(a) (D-71): may the caller set "לחיוב" on a log of this task.
  async getBillableAuthority(ticketId: string): Promise<ApiResponse<{ allowed: boolean }>> {
    return this.client.get('/work-logs/billable-authority', { params: { ticket_id: ticketId } });
  }

  // Decisions 24/28/80 — what a work log would consume (bank / quota / block).
  async getConsumptionPreview(params: { ticket_id: string; minutes: number; start_time?: string; hour_bank_id?: string; work_log_id?: string; billable?: boolean }): Promise<ApiResponse<ConsumptionPreview>> {
    return this.client.get('/work-logs/consumption-preview', { params });
  }

  // --- Stage ח: Hour Bank (§28) ---
  async getHourBank(engagementId: string): Promise<ApiResponse<HourBankSnapshot & Partial<HourBankAggregate>>> {
    return this.client.get(`/engagements/${engagementId}/hour-bank`);
  }

  // Decision 26 — every purchase is a new bank.
  async createHourBankPurchase(engagementId: string, data: { purchased_hours: number; name?: string; purchase_price?: number | null; hourly_rate?: number | null; purchased_at?: string | null; expiry_date?: string | null; warning_threshold_pct?: number; notify_client?: boolean }, idempotencyKey?: string): Promise<ApiResponse<HourBankPurchase>> {
    // DQ-120: the Idempotency-Key of the attempt — a double click never buys (and bills) twice.
    return this.client.post(`/engagements/${engagementId}/hour-bank/purchases`, data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  // Decision 25 — audited expiry extension (authorized users).
  async extendHourBank(engagementId: string, bankId: string, data: { new_expiry: string; reason: string }): Promise<ApiResponse<HourBankPurchase>> {
    return this.client.post(`/engagements/${engagementId}/hour-bank/banks/${bankId}/extend`, data);
  }

  async listHourBankReports(engagementId: string): Promise<ApiResponse<HourBankReport[]>> {
    return this.client.get(`/engagements/${engagementId}/hour-bank/reports`);
  }

  async downloadHourBankReport(engagementId: string, reportId: string): Promise<Blob> {
    return this.client.get(`/engagements/${engagementId}/hour-bank/reports/${reportId}/pdf`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  async updateHourBank(engagementId: string, data: Partial<HourBank>): Promise<ApiResponse<HourBankSnapshot>> {
    // BQ-042/044: an approved purchase charge that stayed after a cancel / a price removal is announced.
    return announceBillingWarnings(await this.client.patch(`/engagements/${engagementId}/hour-bank`, data), data.status === 'cancelled' ? 'cancel' : 'price');
  }

  async listHourBankUsage(engagementId: string, params?: { limit?: number; offset?: number }): Promise<ApiResponse<{ rows: HourBankUsage[]; total: number }>> {
    return this.client.get(`/engagements/${engagementId}/hour-bank/usage`, { params });
  }

  async recordHourBankUsage(engagementId: string, data: { hours: number; used_on?: string; description?: string; ticket_id?: string; bank_id?: string | null; override_reason?: string | null }): Promise<ApiResponse<HourBankUsage>> {
    return this.client.post(`/engagements/${engagementId}/hour-bank/usage`, data);
  }

  async adjustHourBank(engagementId: string, data: { direction: 'credit' | 'debit'; amount_hours: number; reason: string; bank_id?: string | null }): Promise<ApiResponse<HourBankAdjustment>> {
    return this.client.post(`/engagements/${engagementId}/hour-bank/adjustments`, data);
  }

  // §30 — send hour-bank report to customer.
  async sendHourBankReport(engagementId: string, data?: { period_start?: string; period_end?: string; extra_recipients?: string[] }, idempotencyKey?: string): Promise<ApiResponse<HourBankReport>> {
    // DQ-160: the Idempotency-Key of the attempt - a double click never sends the report twice.
    return this.client.post(`/engagements/${engagementId}/hour-bank/report`, data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }

  // BQ-064 (D-76): the customer's address the hour-bank report goes to.
  async getHourBankReportRecipient(engagementId: string): Promise<ApiResponse<{ email: string | null }>> {
    return this.client.get(`/engagements/${engagementId}/hour-bank/report-recipient`);
  }

  // --- Stage ח: לל"ח Service Without Contract (§31) ---
  async getServiceWithoutContract(engagementId: string, params?: { month?: string }): Promise<ApiResponse<ServiceWithoutContract & { engagement_id: string }>> {
    return this.client.get(`/engagements/${engagementId}/service-without-contract`, params ? { params } : undefined);
  }

  async updateServiceWithoutContract(engagementId: string, data: Partial<ServiceWithoutContract>): Promise<ApiResponse<ServiceWithoutContract & { engagement_id: string }>> {
    return this.client.patch(`/engagements/${engagementId}/service-without-contract`, data);
  }

  // --- Stage ח: monthly service-log control (§27) ---
  // SOURCE OF TRUTH for the workflow is reviewId. `yearMonth` is a fetch-or-
  // create bridge that resolves to THE one review for the contract+month.
  async generateServiceLogReview(engagementId: string, month: string): Promise<ApiResponse<ServiceLogReview>> {
    return this.client.post(`/engagements/${engagementId}/service-log/generate`, { month });
  }

  async getServiceLogReview(engagementId: string, yearMonth: string): Promise<ApiResponse<{ review: ServiceLogReview | null; entries: ServiceLogReviewEntry[]; process?: ServiceLogContractProcess }>> {
    return this.client.get(`/engagements/${engagementId}/service-log/${yearMonth}`);
  }

  async listServiceLogReviews(engagementId: string, params?: { limit?: number; offset?: number }): Promise<ApiResponse<ServiceLogReview[]>> {
    return this.client.get(`/engagements/${engagementId}/service-log/reviews`, { params });
  }

  // Confirm the CURRENT session employee's own lines in the review (the server
  // derives the employee from the session — no entry_ids in the body).
  async confirmServiceLogEntry(engagementId: string, reviewId: string): Promise<ApiResponse<ServiceLogReview>> {
    return this.client.post(`/engagements/${engagementId}/service-log/${reviewId}/confirm`);
  }

  async approveServiceLog(engagementId: string, reviewId: string): Promise<ApiResponse<ServiceLogReview>> {
    return this.client.post(`/engagements/${engagementId}/service-log/${reviewId}/approve`);
  }

  async emitServiceLogReport(engagementId: string, reviewId: string): Promise<ApiResponse<ServiceLogReview>> {
    return this.client.post(`/engagements/${engagementId}/service-log/${reviewId}/emit-report`);
  }

  // --- P-10c (D-70): the monthly service-log approvals (/api/service-log) ---
  async getMyServiceLog(month: string): Promise<ApiResponse<ServiceLogEmployeeBreakdown>> {
    return this.client.get(`/service-log/my/${month}`);
  }

  async approveMyServiceLog(month: string): Promise<ApiResponse<{ approved_at: string }>> {
    return this.client.post(`/service-log/my/${month}/approve`);
  }

  async getServiceLogMonth(month: string): Promise<ApiResponse<ServiceLogMonthOverview>> {
    return this.client.get(`/service-log/months/${month}`);
  }

  // D-76: `reason` approves on behalf of employees who have not approved.
  async approveServiceLogCustomer(month: string, organizationId: string, reason?: string): Promise<ApiResponse<ServiceLogCustomerSummary & { on_behalf_of: string[] }>> {
    return this.client.post(`/service-log/months/${month}/customers/${organizationId}/approve`, reason ? { reason } : {});
  }

  async approveServiceLogAll(month: string, reason?: string): Promise<ApiResponse<{ approved: string[]; skipped: string[]; on_behalf_of: Record<string, string[]> }>> {
    return this.client.post(`/service-log/months/${month}/approve-all`, reason ? { reason } : {});
  }

  /** D-76: remind the employees who have not approved (the given ones, or all). */
  async remindServiceLogEmployees(month: string, agentIds?: string[]): Promise<ApiResponse<{ reminded: string[] }>> {
    return this.client.post(`/service-log/months/${month}/remind`, agentIds ? { agent_ids: agentIds } : {});
  }

  async reopenServiceLogEmployee(month: string, agentId: string, reason: string): Promise<ApiResponse<{ reopened: boolean }>> {
    return this.client.post(`/service-log/months/${month}/employees/${agentId}/reopen`, { reason });
  }

  /** D-319 s.10: the customer's PDF (the same one the customer gets), to see before sending. */
  // FIX-SERVICE-TAB: send the stored PDF again after a failed customer e-mail.
  async resendServiceLogCustomerEmail(month: string, organizationId: string): Promise<ApiResponse<ServiceLogCustomerSummary>> {
    return this.client.post(`/service-log/months/${month}/customers/${organizationId}/resend-email`, {});
  }

  async getServiceLogCustomerPdf(month: string, organizationId: string): Promise<Blob> {
    const response = await this.client.get(`/service-log/months/${month}/customers/${organizationId}/pdf`, { responseType: 'blob' });
    return response as any;
  }

  // ===========================================================================
  // Stage י — Documents, foldering, versions, sharing (§38/§39/§40/§41)
  // ===========================================================================

  // Folders
  listDocumentFolders(organizationId: string, parentId?: string | null): Promise<ApiResponse<DocumentFolder[]>> {
    // P-47: `lead:<id>` is an open lead's own folder tree (the explorer's "לידים" branch).
    const leadId = leadScopeId(organizationId);
    if (leadId) {
      // The lead's root folder is the explorer's "top level": it is hidden, and its children move up.
      return this.client.get('/documents/folders', { params: { leadId } }).then((res: any) => {
        const rows: DocumentFolder[] = res?.data ?? [];
        const root = rows.find((f) => f.folder_type === ('lead' as DocumentFolder['folder_type']) && !f.parent_folder_id);
        return root ? { ...res, data: rows.filter((f) => f.id !== root.id).map((f) => (f.parent_folder_id === root.id ? { ...f, parent_folder_id: null } : f)) } : res;
      });
    }
    return this.client.get('/documents/folders', { params: { organizationId, parentId: parentId ?? null } });
  }
  /** P-47 (D-217 s.6): the open leads (and their root folder) the caller may see - the explorer's "לידים" branch. */
  listLeadFolderRoots(): Promise<ApiResponse<Array<{ lead_id: string; name: string; lead_status: string; folder_id: string | null }>>> {
    return this.client.get('/documents/folders/leads');
  }
  /** FIX-B21 (D-214 QX-191): folder names across every customer the caller may browse (matches + ancestors). */
  searchDocumentFolders(q: string, limit = 100): Promise<ApiResponse<Array<DocumentFolder & { organization_name: string | null; is_match: boolean }>>> {
    return this.client.get('/documents/folders/search', { params: { q, limit } });
  }
  /** D-308 (QX-1319): may the caller upload / create a folder / rename / delete in this customer's documents? */
  getDocumentsWriteAccess(organizationId: string, item?: { folderId?: string | null; documentId?: string | null }): Promise<ApiResponse<{ can_write: boolean }>> {
    // D-308b: an explicit can_edit share on the open folder / the document counts too.
    const params: Record<string, string> = { organizationId };
    if (item?.folderId) params.folderId = item.folderId;
    if (item?.documentId) params.documentId = item.documentId;
    return this.client.get('/documents/write-access', { params });
  }
  ensureDocumentFolders(organizationId: string): Promise<ApiResponse<DocumentFolder[]>> {
    if (leadScopeId(organizationId)) return Promise.resolve({ success: true, data: [] } as ApiResponse<DocumentFolder[]>);
    return this.client.get('/documents/folders/ensure', { params: { organizationId } });
  }
  createDocumentFolder(data: { organizationId: string; name: string; parent_folder_id?: string | null; folder_type?: string; description?: string | null }): Promise<ApiResponse<DocumentFolder>> {
    const leadId = leadScopeId(data.organizationId);
    if (leadId) {
      const { organizationId: _o, folder_type: _t, ...rest } = data;
      return this.client.post('/documents/folders', { ...rest, leadId });
    }
    return this.client.post('/documents/folders', data);
  }
  updateDocumentFolder(id: string, data: { name?: string; folder_type?: string; description?: string | null; parent_folder_id?: string | null }): Promise<ApiResponse<DocumentFolder>> {
    return this.client.patch(`/documents/folders/${id}`, data);
  }
  deleteDocumentFolder(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/documents/folders/${id}`);
  }

  // Documents
  listDocuments(params?: { organizationId?: string; folderId?: string | null; status?: string }): Promise<ApiResponse<OteDocument[]>> {
    const leadId = leadScopeId(params?.organizationId);
    if (leadId) {
      return this.client.get('/documents', { params: { ...params, organizationId: undefined, leadId } }).then((res: any) => {
        const rows: OteDocument[] = res?.data ?? [];
        // A document in the lead's root folder is at the explorer's top level.
        const top = (d: OteDocument & { folder_type?: string; folder_parent_id?: string | null }) =>
          d.folder_type === 'lead' && !d.folder_parent_id;
        return { ...res, data: rows.map((d) => (top(d as never) ? { ...d, folder_id: null } : d)) };
      });
    }
    return this.client.get('/documents', { params });
  }
  getDocumentBundle(id: string): Promise<ApiResponse<OteDocument>> {
    return this.client.get(`/documents/${id}`);
  }
  createDocument(data: OteDocumentInput): Promise<ApiResponse<OteDocument>> {
    const leadId = leadScopeId(data.organization_id);
    if (leadId) return this.client.post('/documents', { ...data, organization_id: undefined, lead_id: leadId });
    return this.client.post('/documents', data);
  }
  updateDocument(id: string, data: Partial<OteDocumentInput>): Promise<ApiResponse<OteDocument>> {
    return this.client.patch(`/documents/${id}`, data);
  }
  deleteDocument(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/documents/${id}`);
  }
  checkDocumentAccess(id: string, mode?: 'view' | 'edit'): Promise<ApiResponse<{ id: string; status: string; access: string }>> {
    return this.client.get(`/documents/${id}/access`, { params: { mode } });
  }

  // Versions
  listDocumentVersions(id: string): Promise<ApiResponse<DocumentVersion[]>> {
    return this.client.get(`/documents/${id}/versions`);
  }
  /**
   * Upload a new file version (multipart). Previous versions are always kept;
   * on a signed document the server starts a new signing process.
   */
  uploadDocumentVersion(id: string, file: File, versionNote?: string): Promise<ApiResponse<DocumentVersion>> {
    const form = new FormData();
    form.append('file', file);
    if (versionNote) form.append('version_note', versionNote);
    return this.client.post(`/documents/${id}/versions`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  }
  downloadDocumentVersion(id: string, versionId: string, fallbackName: string): Promise<void> {
    return this.downloadFile(`/documents/${id}/versions/${versionId}/download`, fallbackName);
  }

  /** Q-DQ-295: opens an image / PDF version in a new tab (same endpoint and permissions as the download). */
  async previewDocumentVersion(id: string, versionId: string): Promise<void> {
    return this.previewFileInNewTab(`/documents/${id}/versions/${versionId}/download`);
  }

  /** Q-DQ-295: the same preview for a customer-visible, published document in the portal (same endpoint and permissions as its download). */
  async previewPortalDocument(id: string): Promise<void> {
    return this.previewFileInNewTab(`/portal/documents/${id}/download`);
  }

  private async previewFileInNewTab(path: string): Promise<void> {
    // The tab is opened synchronously (inside the click) so the browser does not block it as a pop-up.
    const tab = window.open('', '_blank');
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        let message = `${response.status}`;
        try { message = (await response.json())?.error?.message || message; } catch { /* non-JSON error body */ }
        throw { success: false, error: { message }, message, response: { status: response.status } };
      }
      const blob = await response.blob();
      if (!tab) { saveBlobAs(blob, 'document'); return; }
      tab.opener = null;
      tab.location.href = URL.createObjectURL(blob);
    } catch (err) {
      tab?.close();
      throw err;
    }
  }

  // Sharing
  grantDocumentPermission(id: string, data: { employee_id?: string | null; user_id?: string | null; can_view?: boolean; can_edit?: boolean }): Promise<ApiResponse<DocumentPermission>> {
    return this.client.post(`/documents/${id}/permissions`, data);
  }
  listDocumentPermissions(id: string): Promise<ApiResponse<DocumentPermission[]>> {
    return this.client.get(`/documents/${id}/permissions`);
  }
  revokeFolderPermission(folderId: string, data: { employee_id?: string | null; user_id?: string | null }): Promise<ApiResponse<{ id: string; removed: boolean }>> {
    return this.client.delete(`/documents/folders/${folderId}/permissions`, { data });
  }
  grantFolderPermission(folderId: string, data: { employee_id?: string | null; user_id?: string | null; can_view?: boolean; can_edit?: boolean }): Promise<ApiResponse<{ id: string }>> {
    return this.client.post(`/documents/folders/${folderId}/permissions`, data);
  }

  // Metadata
  setDocumentMetadata(id: string, entries: Record<string, string>): Promise<ApiResponse<void>> {
    return this.client.post(`/documents/${id}/metadata`, entries);
  }

  // Signatures (§41)
  transitionDocumentStatus(id: string, status: DocumentStatus): Promise<ApiResponse<void>> {
    return this.client.post(`/documents/${id}/status`, { status });
  }
  signDocumentVersion(id: string, data: { version_id: string; signer_email?: string; signer_name?: string; content_base64?: string }): Promise<ApiResponse<{ providerSignatureId: string; signedAt: string }>> {
    return this.client.post(`/documents/${id}/sign`, data);
  }
  verifyDocumentSignature(id: string): Promise<ApiResponse<{ valid: boolean }>> {
    return this.client.get(`/documents/${id}/signature/verify`);
  }


  // ===========================================================================
  // Stage י — Project delivery (§42) + customer testing period (§43)
  // ===========================================================================
  listDeliveries(params?: { projectId?: string; developmentId?: string }): Promise<ApiResponse<ProjectDelivery[]>> {
    return this.client.get('/deliveries', { params });
  }
  createDelivery(data: { project_id?: string; development_id?: string; scope_notes?: string; testing_period_start_date?: string }): Promise<ApiResponse<ProjectDelivery>> {
    return this.client.post('/deliveries', data);
  }
  getDeliveryDetail(id: string): Promise<ApiResponse<ProjectDelivery & { checklists: DeliveryChecklistItem[] }>> {
    return this.client.get(`/deliveries/${id}`);
  }
  advanceDeliveryStatus(id: string): Promise<ApiResponse<ProjectDelivery>> {
    return this.client.post(`/deliveries/${id}/advance`);
  }
  setDeliveryStatus(id: string, status: DeliveryStatus): Promise<ApiResponse<ProjectDelivery>> {
    return this.client.patch(`/deliveries/${id}/status`, { status });
  }
  updateDeliveryNotes(id: string, data: { scope_notes?: string; internal_qa_notes?: string; customer_check_notes?: string }): Promise<ApiResponse<ProjectDelivery>> {
    return this.client.patch(`/deliveries/${id}/notes`, data);
  }
  setDeliveryTestingPeriod(id: string, data: { start?: string; end?: string }): Promise<ApiResponse<ProjectDelivery>> {
    return this.client.patch(`/deliveries/${id}/testing-period`, data);
  }
  addDeliveryChecklistItem(id: string, data: { checklist_type: 'qa' | 'customer'; item_text: string; sort_order?: number }): Promise<ApiResponse<DeliveryChecklistItem>> {
    return this.client.post(`/deliveries/${id}/checklist-items`, data);
  }
  toggleDeliveryChecklistItem(itemId: string): Promise<ApiResponse<DeliveryChecklistItem>> {
    return this.client.patch(`/deliveries/checklist-items/${itemId}/toggle`);
  }
  deleteDeliveryChecklistItem(itemId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/deliveries/checklist-items/${itemId}`);
  }

  // ===========================================================================
  // Stage יא — Billing obligations / Morning / Debts (§44-§49)
  // ===========================================================================
  listObligations(params?: {
    organization_id?: string;
    engagement_id?: string;
    status?: 'pending' | 'partial' | 'paid';
    debt_only?: boolean;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ rows: Obligation[]; total: number }>> {
    return this.client.get('/billing', { params });
  }
  getObligationDetail(id: string): Promise<ApiResponse<Obligation & { items: BillingItem[]; events: ObligationEvent[] }>> {
    return this.client.get(`/billing/${id}`);
  }
  createObligation(data: {
    organization_id: string;
    engagement_id?: string;
    obligation_type: ObligationType;
    title?: string;
    description?: string;
    amount?: number;
    currency?: string;
    issue_date: string;
    due_date?: string;
    period_start?: string;
    period_end?: string;
    payment_terms?: string;
    items?: Array<{ description?: string; quantity?: number; unit?: string; rate?: number; amount: number; source?: string; source_ref?: string; item_date?: string }>;
  }, idempotencyKey?: string): Promise<ApiResponse<Obligation>> {
    // DQ-159: the Idempotency-Key of the attempt — a double click never creates two charges.
    return this.client.post('/billing', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }
  addObligationItem(id: string, data: { description?: string; quantity?: number; unit?: string; rate?: number; amount: number; source?: string; source_ref?: string; item_date?: string }): Promise<ApiResponse<{ item: BillingItem; obligation: Obligation | null }>> {
    return this.client.post(`/billing/${id}/items`, data);
  }
  markObligationPaid(id: string, data: { external_accounting_reference: string; note?: string }): Promise<ApiResponse<Obligation>> {
    return this.client.post(`/billing/${id}/paid`, data);
  }
  // --- Admin diagnostics (decision 144) — GM only ---
  getAdminDiagnostics(): Promise<ApiResponse<DiagnosticsData>> {
    return this.client.get('/admin/diagnostics');
  }
  // Decisions 63/64 — (partial) payments, idempotent on provider + external id.
  recordPayment(id: string, data: { amount: number; currency?: string; paid_at?: string | null; method?: string | null; provider?: string; external_id?: string | null; notes?: string | null }): Promise<ApiResponse<{ payment: Payment; obligation: Obligation; duplicate: boolean }>> {
    return this.client.post(`/billing/${id}/payments`, data);
  }
  // Decisions 34/39 — signed adjustment with reason.
  addObligationAdjustment(id: string, data: { amount: number; reason: string; billing_item_id?: string | null }, idempotencyKey?: string): Promise<ApiResponse<{ adjustment: BillingAdjustment; obligation: Obligation }>> {
    // DQ-158: the Idempotency-Key of the attempt — a double click never records two adjustments.
    return this.client.post(`/billing/${id}/adjustments`, data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
  }
  issueObligation(id: string): Promise<ApiResponse<Obligation>> {
    return this.client.post(`/billing/${id}/issue`);
  }
  getDebtAging(params?: { organization_id?: string; as_of?: string }): Promise<ApiResponse<DebtAging>> {
    return this.client.get('/billing/aging', { params });
  }
  listPaymentProviders(): Promise<ApiResponse<PaymentProviderInfo[]>> {
    return this.client.get('/billing/payment-providers');
  }
  // Decision 74 — waiting-for-customer SLA pause.
  getTicketSla(ticketId: string): Promise<ApiResponse<{ sla_paused_at: string | null; sla_paused_minutes: number; pauses: unknown[] }>> {
    return this.client.get(`/tickets/${ticketId}/sla`);
  }
  pauseTicketSla(ticketId: string, reason?: string): Promise<ApiResponse<{ paused: boolean; paused_at: string | null }>> {
    return this.client.post(`/tickets/${ticketId}/sla/pause`, { reason });
  }
  resumeTicketSla(ticketId: string): Promise<ApiResponse<{ resumed: boolean; paused_business_minutes: number }>> {
    return this.client.post(`/tickets/${ticketId}/sla/resume`);
  }
  getOrganizationBilling(orgId: string): Promise<ApiResponse<OrganizationBilling>> {
    return this.client.get(`/billing/organizations/${orgId}`);
  }

  // ===========================================================================
  // u4 — Sales & documents
  // ===========================================================================

  /**
   * Authenticated file download (Bearer → blob → browser save). Uses fetch so
   * the server's Content-Disposition file name is available; a failed download
   * rejects with the same `{ error: { message } }` shape as other API errors.
   */
  private async downloadFile(path: string, fallbackName: string): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${API_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = `${response.status}`;
      try {
        const body = await response.json();
        message = body?.error?.message || message;
      } catch { /* non-JSON error body */ }
      return Promise.reject({ success: false, error: { message }, message, response: { status: response.status } });
    }
    const blob = await response.blob();
    saveBlobAs(blob, fileNameFromDisposition(response.headers.get('content-disposition')) || fallbackName);
  }

  // --- Customer hub ---
  getCustomerHubSummary(orgId: string): Promise<ApiResponse<CustomerHubSummary>> {
    return this.client.get(`/customer-hub/${orgId}/summary`);
  }
  /** D-321 - the "עבודה פנימית OTE" hub overview. */
  getInternalHubOverview(): Promise<ApiResponse<InternalHubOverview>> {
    return this.client.get('/customer-hub/internal/overview');
  }
  getCustomerWorkSummary(orgId: string, params?: { from?: string; to?: string }): Promise<ApiResponse<CustomerWorkSummary>> {
    return this.client.get(`/customer-hub/${orgId}/work-summary`, { params });
  }

  // --- P-07 (D-50, D-74) — the "אפיון" card ---
  getCharacterizations(organizationId: string): Promise<ApiResponse<Characterization[]>> {
    return this.client.get('/characterizations', { params: { organization_id: organizationId } });
  }
  /** D-77 — the "+ אפיון" window: card + task "אפיון — <name>" + 2 × 50% in one transaction. */
  createCharacterization(data: {
    organization_id: string;
    title: string;
    /** Before VAT; split 50/50. */
    price: number;
    quote_id?: string | null;
    assignee_ids?: string[];
    notes?: string | null;
  }): Promise<ApiResponse<Characterization>> {
    return this.client.post('/characterizations', data);
  }
  updateCharacterization(id: string, data: { title?: string; price?: number | null; notes?: string | null }): Promise<ApiResponse<Characterization>> {
    return this.client.patch(`/characterizations/${id}`, data);
  }
  completeCharacterization(id: string): Promise<ApiResponse<Characterization>> {
    return this.client.post(`/characterizations/${id}/complete`);
  }
  cancelCharacterization(id: string, reason: string): Promise<ApiResponse<Characterization>> {
    return this.client.post(`/characterizations/${id}/cancel`, { reason });
  }
  /** D-74 §3 — GM / Account Manager only; audited on the server. */
  setCharacterizationPayment(id: string, index: 1 | 2, paid: boolean, note?: string): Promise<ApiResponse<Characterization>> {
    return this.client.post(`/characterizations/${id}/payments/${index}/${paid ? 'paid' : 'unpaid'}`, note ? { note } : {});
  }

  /** D-262 (QX-1243) — the manual release of the charge's lock after "שולם" was cancelled (GM / Account Manager; audited). */
  releaseCharacterizationPaymentLock(id: string, index: 1 | 2): Promise<ApiResponse<Characterization>> {
    return this.client.post(`/characterizations/${id}/payments/${index}/release-lock`, {});
  }

  // --- Quote lifecycle ---
  /** D-08 — "סמן כנחתמה" (manual signature fact; a characterization quote opens its card). */
  markQuoteSigned(id: string, note?: string): Promise<ApiResponse<Quote & { conversion?: QuoteConversionOutcome; characterization_id?: string | null }>> {
    return this.client.post(`/quotes/${id}/mark-signed`, note ? { note } : {});
  }
  transitionQuote(id: string, action: QuoteTransitionAction, reason?: string): Promise<ApiResponse<Quote & { conversion?: QuoteConversionOutcome }>> {
    return this.client.post(`/quotes/${id}/transition`, reason ? { action, reason } : { action });
  }
  /** FIX-B35 (QX-651): send again the e-mail of a sent lead quote whose mail did not leave. */
  resendLeadQuoteMail(id: string): Promise<ApiResponse<{ lead_mail: { sent: boolean; to: string; reason?: 'no_email' | 'failed' } }>> {
    return this.client.post(`/quotes/${id}/resend-lead-mail`);
  }
  /** Create a new draft revision from a sent/viewed quote; the old one becomes `superseded`. */
  reviseQuote(id: string): Promise<ApiResponse<Quote>> {
    return this.client.post(`/quotes/${id}/revise`);
  }
  createContractFromQuote(id: string, data?: { title?: string; start_date?: string; end_date?: string }): Promise<ApiResponse<Contract>> {
    return this.client.post(`/quotes/${id}/contract`, data ?? {});
  }
  /** Quote PDF (§37) — authenticated blob download with the server's file name. */
  downloadQuotePdf(id: string, fallbackName: string): Promise<void> {
    return this.downloadFile(`/quotes/${id}/pdf`, fallbackName);
  }

  // --- Portal quotes + document download ---
  getPortalQuotes(params?: { organization_id?: string }): Promise<ApiResponse<PortalQuote[]>> {
    return this.client.get('/portal/quotes', { params });
  }
  getPortalQuote(id: string): Promise<ApiResponse<PortalQuoteDetail>> {
    return this.client.get(`/portal/quotes/${id}`);
  }
  acceptPortalQuote(id: string): Promise<ApiResponse<PortalQuote>> {
    return this.client.post(`/portal/quotes/${id}/accept`);
  }
  rejectPortalQuote(id: string, reason?: string): Promise<ApiResponse<PortalQuote>> {
    return this.client.post(`/portal/quotes/${id}/reject`, reason ? { reason } : {});
  }
  downloadPortalDocument(id: string, fallbackName: string): Promise<void> {
    return this.downloadFile(`/portal/documents/${id}/download`, fallbackName);
  }

  // --- Contracts ---
  listContracts(params?: { organization_id?: string; quote_id?: string; engagement_id?: string; status?: ContractStatus }): Promise<ApiResponse<Contract[]>> {
    return this.client.get('/contracts', { params });
  }
  getContract(id: string): Promise<ApiResponse<ContractDetail>> {
    return this.client.get(`/contracts/${id}`);
  }
  createContract(data: {
    organization_id: string;
    title: string;
    terms?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    parties?: ContractParty[];
    quote_id?: string;
    engagement_id?: string;
  }): Promise<ApiResponse<Contract>> {
    return this.client.post('/contracts', data);
  }
  updateContract(id: string, data: ContractInput): Promise<ApiResponse<Contract>> {
    return this.client.patch(`/contracts/${id}`, data);
  }
  transitionContract(id: string, status: ContractStatus, reason?: string): Promise<ApiResponse<Contract>> {
    return this.client.post(`/contracts/${id}/transition`, reason ? { status, reason } : { status });
  }
  deleteContract(id: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/contracts/${id}`);
  }

  // --- Documents: upload, publish, links ---
  /** Create a document together with its first file version (multipart). */
  createDocumentWithFile(fields: {
    organization_id: string;
    title: string;
    folder_id?: string | null;
    document_type?: string;
    description?: string | null;
    classification?: DocumentClassification;
    version_note?: string | null;
  }, file: File): Promise<ApiResponse<OteDocument>> {
    const form = new FormData();
    const leadId = leadScopeId(fields.organization_id);
    for (const [k, v] of Object.entries(leadId ? { ...fields, organization_id: undefined, lead_id: leadId } : fields)) {
      if (v !== undefined && v !== null && v !== '') form.append(k, String(v));
    }
    form.append('file', file);
    return this.client.post('/documents', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  }
  publishDocument(id: string, published: boolean, reason?: string): Promise<ApiResponse<OteDocument>> {
    return this.client.post(`/documents/${id}/publish`, reason ? { published, reason } : { published });
  }
  listDocumentLinks(id: string): Promise<ApiResponse<DocumentLink[]>> {
    return this.client.get(`/documents/${id}/links`);
  }
  addDocumentLink(id: string, data: { entity_type: DocumentLinkEntityType; entity_id: string }): Promise<ApiResponse<DocumentLink>> {
    return this.client.post(`/documents/${id}/links`, data);
  }
  removeDocumentLink(id: string, linkId: string): Promise<ApiResponse<void>> {
    return this.client.delete(`/documents/${id}/links/${linkId}`);
  }
  listDocumentsByEntity(entityType: DocumentLinkEntityType, entityId: string): Promise<ApiResponse<OteDocument[]>> {
    return this.client.get('/documents/by-entity', { params: { entity_type: entityType, entity_id: entityId } });
  }

  // --- Workspace calendar journal (the caller's own entries) ---
  getWorkspaceJournal(params?: { from?: string; to?: string }): Promise<ApiResponse<WorkspaceJournalEntryDto[]>> {
    return this.client.get('/workspace/journal', { params });
  }
  saveWorkspaceJournalEntry(id: string, data: { entry_date: string; content: string }): Promise<ApiResponse<WorkspaceJournalEntryDto>> {
    return this.client.put(`/workspace/journal/${id}`, data);
  }
  importWorkspaceJournal(entries: Array<{ id: string; entry_date: string; content: string; created_at?: string | null }>): Promise<ApiResponse<WorkspaceJournalEntryDto[]>> {
    return this.client.put('/workspace/journal', { entries });
  }
  deleteWorkspaceJournalEntry(id: string): Promise<ApiResponse<{ id: string }>> {
    return this.client.delete(`/workspace/journal/${id}`);
  }
  // --- P-63 (D-239): install-the-extension button + one-time hand-off ---
  getExtensionInfo(): Promise<ApiResponse<{ kind: 'employee' | 'customer'; latest_version: string; store_url: string | null; download_available: boolean }>> {
    return this.client.get('/extension/info');
  }
  createExtensionHandoff(kind: 'employee' | 'customer'): Promise<ApiResponse<{ token: string; expires_in: number }>> {
    return this.client.post('/extension/handoff', { kind });
  }
  async downloadExtensionZip(): Promise<Blob> {
    const response = await this.client.get('/extension/download', { responseType: 'blob' });
    return response as unknown as Blob;
  }
}

export const api = new ApiService();

/** P-05/C — the milestone form's body (create; PATCH takes the same fields). */
export interface MilestoneFormBody {
  name: string;
  description?: string | null;
  responsible_user_id?: string | null;
  assignee_ids?: string[];
  start_date?: string | null;
  due_date?: string | null;
  status?: string;
  planned_hours_mode?: 'undefined' | 'automatic' | 'manual';
  planned_hours_manual?: number | null;
  parent_milestone_id?: string | null;
}

/** P-05/F — the bulk add tree. */
export interface BulkAddTaskBody { title: string; assignee_ids?: string[]; estimated_hours?: number | string | null; due_date?: string | null }
export interface BulkAddSubBody { existing_id?: string; name?: string; due_date?: string | null; tasks?: BulkAddTaskBody[] }
export interface BulkAddMilestoneBody extends BulkAddSubBody { subs?: BulkAddSubBody[] }
export interface BulkAddBody {
  milestones?: BulkAddMilestoneBody[];
  tasks?: BulkAddTaskBody[];
  /** D-75 — the one answer to "לשייך גם?" for the whole group. */
  also_assign_parents?: boolean;
}
